import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Row, Col, Card, Table, Button, Badge, Form, Modal, Pagination, Tabs, Tab, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Ticket, PurchaseRequest, Workflow, Group, WorkflowCustomField, User, DocumentInstance, TicketTemplate } from '../types';
import { API_URL } from '../config';
import './Dashboard.css';
import useAlert from '../hooks/useAlert';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { useAuth } from '../context/AuthContext';
import { useArea } from '../context/AreaContext';
import DraftListModal from '../components/DraftListModal';
import FilterPanel, {
  ActiveFilter,
  AssigneePeerOption,
  FilterAssigneeCombobox,
  FilterControl,
  FilterDropdownSelect,
  FilterField,
  FilterInputGroup,
  FilterSection,
  FilterToggle,
} from '../components/filters/FilterPanel';
import StatusMultiFilter from '../components/filters/StatusMultiFilter';
import {
  restoreDashboardStatuses,
  serializeDashboardParams,
  statusesForArea,
} from '../utils/dashboardStatusFilter';
import {
  TITLE_COLUMN_STORAGE_KEY,
  clampTitleColumnWidth,
  readTitleColumnWidth,
} from '../utils/dashboardTitleColumn';
import CustomTableField from '../components/CustomTableField';
import SupplierFieldControl from '../components/SupplierFieldControl';
import { createDefaultTableValue, hasTableContent, serializeTableValue } from '../utils/tableCustomField';
import { formatFastApiValidationErrors } from '../utils/apiError';
import { shouldShowConditionalField } from '../utils/conditionalField';
import { formatCurrencyInputDisplay, normalizeCurrencyInput } from '../utils/currencyInput';
import { pickValueRangeValidationForNumericValue, coerceMinDateDaysValue } from '../utils/valueRangeValidation';
import {
  findWorkflowFieldById,
  parseValidationRulesJson,
  isValidationSourceValueEmpty,
  parseCustomFieldNumericValue,
} from '../utils/customFieldValidation';
import {
  addDaysLocal,
  formatDateInputLocal,
  parseDateInputLocal,
  shouldPrefillDateField,
  startOfTodayLocal,
} from '../utils/customFieldDate';
import { searchActiveUsers, searchPurchaseFormUsers } from '../utils/searchUsers';
import { getAuthToken } from '../utils/authToken';
import {
  URGENCY_OPTIONS,
  type TicketUrgency,
  getUrgencyColor,
  getUrgencyLabel,
} from '../utils/urgency';
import { getPriorityLabel } from '../utils/priority';
import { canManagePurchasePriority, canManageTicketPriority, isEndUser } from '../utils/roles';
import {
  createTicketTemplate,
  deleteTicketTemplate,
  listTicketTemplateCategories,
  listTicketTemplates,
} from '../services/ticketTemplates';
import {
  ATTACHMENT_ACCEPT,
  validateAttachmentFile,
  validateAttachmentFiles,
} from '../utils/uploadValidation';
import {
  getSupplierSelectionCount,
  isSupplierField as isSupplierCustomField,
  validateSupplierSelectionValue,
} from '../utils/supplierSelection';
import { getPurchaseDisplayId, getPurchaseDisplaySortKey } from '../utils/purchaseDisplayId';
import {
  hasSavedCustomFieldFile,
  parseSavedCustomFieldFilesFromApi,
  savedCustomFieldFileLabel,
  uploadPurchaseCustomFieldFiles,
  type SavedCustomFieldFile,
} from '../utils/purchaseCustomFieldFiles';
import {
  hasCheckFieldPersistedValue,
  hydrateCustomFieldTextValue,
  serializeCheckFieldValue,
} from '../utils/purchaseCustomFieldValues';
import {
  upsertPurchaseCustomFieldValue,
  upsertCustomFieldValue,
  batchUpsertPurchaseCustomFieldValues,
  isBatchEndpointUnavailable,
} from '../utils/purchaseCustomFieldUpsert';
import { partitionFieldsForSave } from '../utils/customFieldSaveOrder';
import {
  buildPurchaseDraftPayload,
  purchaseDraftFinalizeUrl,
  purchaseDraftItemUrl,
  PURCHASE_DRAFT_STATUS,
  savePurchaseDraftRecord,
  throwNotADraftForSubmit,
} from '../utils/purchaseDraftApi';
import {
  CLOSED_VISIBILITY_OPTIONS,
  ClosedVisibility,
  DEFAULT_CLOSED_VISIBILITY,
  closedVisibilityChipLabel,
  closedVisibilityNotice,
  normalizeClosedVisibility,
} from '../utils/closedItemsVisibility';

const ASSIGNED_PEER_SESSION_PREFIX = 'dashboard_filter_assigned_peer_id';
const DASHBOARD_ITEMS_PER_PAGE_STORAGE_KEY = 'dashboard_items_per_page';
const DASHBOARD_FILTERS_SESSION_PREFIX = 'dashboard_filters';
const ALLOWED_ITEMS_PER_PAGE = [5, 10, 20, 50, 100] as const;
type DashboardItem = Ticket | PurchaseRequest;

function assignedPeerSessionKey(userId: number, area: string) {
  return `${ASSIGNED_PEER_SESSION_PREFIX}:${userId}:${area}`;
}

function dashboardFiltersSessionKey(area: string) {
  return `${DASHBOARD_FILTERS_SESSION_PREFIX}:${area}`;
}

function getInitialDashboardItemsPerPage(): number {
  if (typeof window === 'undefined') return 10;
  const raw = window.localStorage.getItem(DASHBOARD_ITEMS_PER_PAGE_STORAGE_KEY);
  if (!raw) return 10;
  const parsed = Number(raw);
  return ALLOWED_ITEMS_PER_PAGE.includes(parsed as typeof ALLOWED_ITEMS_PER_PAGE[number]) ? parsed : 10;
}

/** Agrega contagens por status vindas do endpoint dashboard-summary (SQL no backend). */
function statsFromCountsByStatus(total: number, countsByStatus: Record<string, number>) {
  const c = countsByStatus || {};
  let em_andamento = c['em_andamento'] ?? 0;
  for (const k of Object.keys(c)) {
    if (k.startsWith('aguardando_')) em_andamento += c[k] ?? 0;
  }
  return {
    total,
    aberto: (c['novo'] ?? 0) + (c['aberto'] ?? 0),
    em_andamento,
    resolvido: (c['resolvido'] ?? 0) + (c['fechado'] ?? 0),
    novo: c['novo'] ?? 0,
    cancelado: c['cancelado'] ?? 0,
  };
}

type TitleColumnHeaderProps = {
  width: number;
  sortActive: boolean;
  sortIcon: React.ReactNode;
  onSort: () => void;
  onResizeStart: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

const TitleColumnHeader: React.FC<TitleColumnHeaderProps> = ({
  width,
  sortActive,
  sortIcon,
  onSort,
  onResizeStart,
}) => (
  <th className="dashboard-title-col" style={{ width, minWidth: width, maxWidth: width }}>
    <div
      className={`sortable-header d-flex align-items-center ${sortActive ? 'active' : ''}`}
      onClick={onSort}
    >
      Título
      {sortIcon}
    </div>
    <button
      type="button"
      className="dashboard-title-resize-handle"
      aria-label="Redimensionar coluna Título"
      onMouseDown={onResizeStart}
      onClick={(event) => event.stopPropagation()}
    />
  </th>
);

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { currentArea } = useArea();
  const canSeeTicketPriority = useMemo(
    () =>
      currentArea === 'purchases'
        ? canManagePurchasePriority(user)
        : canManageTicketPriority(user),
    [currentArea, user]
  );
  const [stats, setStats] = useState({
    total: 0,
    aberto: 0,
    em_andamento: 0,
    resolvido: 0,
    novo: 0,
    cancelado: 0,
  });
  const [allTickets, setAllTickets] = useState<DashboardItem[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [myGroupPeers, setMyGroupPeers] = useState<AssigneePeerOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshingData, setIsRefreshingData] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterStatuses, setFilterStatuses] = useState<string[]>([]);
  const [filterUrgency, setFilterUrgency] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterWorkflow, setFilterWorkflow] = useState('');
  const [filterGroup, setFilterGroup] = useState<string>('');
  const [filterAssignedToPeerId, setFilterAssignedToPeerId] = useState<number | null>(null);
  const [assignedPeerSearch, setAssignedPeerSearch] = useState('');
  const [searchText, setSearchText] = useState('');
  const [searchInputText, setSearchInputText] = useState('');
  const [includeThirdPartyTickets, setIncludeThirdPartyTickets] = useState<boolean>(true);
  const [showSubtasks, setShowSubtasks] = useState(false);
  const [filterCreatedByMe, setFilterCreatedByMe] = useState(false);
  const [closedVisibility, setClosedVisibility] = useState<ClosedVisibility>(DEFAULT_CLOSED_VISIBILITY);
  const [hiddenConcludedTotal, setHiddenConcludedTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(getInitialDashboardItemsPerPage);
  const [titleColumnWidth, setTitleColumnWidth] = useState(readTitleColumnWidth);
  const [totalTickets, setTotalTickets] = useState(0);
  const [sortColumn, setSortColumn] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [filterInitialized, setFilterInitialized] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const { showAlert, AlertComponent } = useAlert();
  
  // Ordenar tickets no frontend (sem recarregar dados)
  const sortedTickets = useMemo(() => {
    return [...allTickets].sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      switch (sortColumn) {
        case 'id':
          const isPurchaseInIncidentArea =
            currentArea === 'tickets' && (a as PurchaseRequest).item_source === 'purchase_subtask';
          if (currentArea === 'purchases' || isPurchaseInIncidentArea) {
            aValue = getPurchaseDisplaySortKey(a as PurchaseRequest);
            bValue = getPurchaseDisplaySortKey(b as PurchaseRequest);
          } else {
            aValue = a.id;
            bValue = b.id;
          }
          break;
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'urgency': {
          const rank: Record<string, number> = { ALTA: 0, MEDIA: 1, BAIXA: 2 };
          const au =
            currentArea === 'tickets'
              ? String((a as Ticket).urgency ?? '').toUpperCase()
              : '';
          const bu =
            currentArea === 'tickets'
              ? String((b as Ticket).urgency ?? '').toUpperCase()
              : '';
          aValue = au && rank[au] !== undefined ? rank[au] : 3;
          bValue = bu && rank[bu] !== undefined ? rank[bu] : 3;
          break;
        }
        case 'priority':
          const priorityOrder: { [key: string]: number } = {
            'critica': 4,
            'alta': 3,
            'media': 2,
            'baixa': 1
          };
          aValue = priorityOrder[a.priority ?? ''] || 0;
          bValue = priorityOrder[b.priority ?? ''] || 0;
          break;
        case 'creator':
          aValue = (a.creator?.name || '').toLowerCase();
          bValue = (b.creator?.name || '').toLowerCase();
          break;
        case 'created_at':
        default:
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [allTickets, sortColumn, sortDirection, currentArea]);

  /** Membros dos grupos em que o utilizador atual participa (via /groups/my-peers) */
  const myGroupPeerOptions = myGroupPeers;

  const filteredPeerSuggestions = useMemo(() => {
    const q = assignedPeerSearch.trim().toLowerCase();
    const list = myGroupPeerOptions;
    if (!q) return list.slice(0, 20);
    return list
      .filter(
        (p) =>
          (p.user_name || '').toLowerCase().includes(q) ||
          (p.user_email || '').toLowerCase().includes(q)
      )
      .slice(0, 20);
  }, [myGroupPeerOptions, assignedPeerSearch]);

  const workflowFilterOptions = useMemo(
    () =>
      workflows
        .filter((w) => w.is_active)
        .map((w) => ({ value: String(w.id), label: w.name })),
    [workflows],
  );

  const groupFilterOptions = useMemo(
    () =>
      groups
        .filter((g) => g.is_active)
        .map((g) => ({ value: String(g.id), label: g.name })),
    [groups],
  );

  const priorityFilterOptions = useMemo(
    () => [
      { value: 'p1', label: 'P1' },
      { value: 'p2', label: 'P2' },
      { value: 'p3', label: 'P3' },
      { value: 'p4', label: 'P4' },
    ],
    [],
  );

  const closedVisibilityFilterOptions = useMemo(
    () => CLOSED_VISIBILITY_OPTIONS.map((option) => ({ value: option.value, label: option.label })),
    [],
  );

  // Carregar filtro de grupo salvo do usuário ao montar o componente
  // IMPORTANTE: Isso deve acontecer ANTES da primeira consulta
  useEffect(() => {
    if (user && !filterInitialized) {
      let restored: any = null;
      try {
        const raw = sessionStorage.getItem(dashboardFiltersSessionKey(currentArea));
        restored = raw ? JSON.parse(raw) : null;
      } catch {
        restored = null;
      }

      if (restored) {
        setFilterStatuses(restoreDashboardStatuses(restored));
        setFilterUrgency(restored.filterUrgency || '');
        setFilterPriority(restored.filterPriority || '');
        setFilterWorkflow(restored.filterWorkflow || '');
        setFilterGroup(restored.filterGroup || '');
        setFilterAssignedToPeerId(
          typeof restored.filterAssignedToPeerId === 'number' ? restored.filterAssignedToPeerId : null
        );
        setSearchText(restored.searchText || '');
        setSearchInputText(restored.searchText || '');
        setShowSubtasks(Boolean(restored.showSubtasks));
        setFilterCreatedByMe(Boolean(restored.filterCreatedByMe));
        setClosedVisibility(normalizeClosedVisibility(restored.closedVisibility));
        setCurrentPage(1);
        if (currentArea === 'tickets' && typeof restored.includeThirdPartyTickets === 'boolean') {
          setIncludeThirdPartyTickets(restored.includeThirdPartyTickets);
        }
      } else {
        // Se o usuário tem um filtro salvo, aplicar; caso contrário, deixar vazio
        if (user.dashboard_group_filter) {
          setFilterGroup(String(user.dashboard_group_filter));
        } else {
          setFilterGroup(''); // Garantir que está vazio se não houver filtro salvo
        }
        if (user.id) {
          const peerRaw = sessionStorage.getItem(assignedPeerSessionKey(user.id, currentArea));
          if (peerRaw) {
            const pid = parseInt(peerRaw, 10);
            if (!Number.isNaN(pid)) setFilterAssignedToPeerId(pid);
          }
        }
      }
      setFilterInitialized(true);
    }
  }, [user, filterInitialized, currentArea]);

  // Ao mudar de área (Tickets / Compras), restaurar preferência de sessão dessa área
  useEffect(() => {
    if (!user?.id || !filterInitialized) return;
    const peerRaw = sessionStorage.getItem(assignedPeerSessionKey(user.id, currentArea));
    if (peerRaw) {
      const pid = parseInt(peerRaw, 10);
      if (!Number.isNaN(pid)) setFilterAssignedToPeerId(pid);
      else {
        setFilterAssignedToPeerId(null);
        setAssignedPeerSearch('');
      }
    } else {
      setFilterAssignedToPeerId(null);
      setAssignedPeerSearch('');
    }
  }, [currentArea, user?.id, filterInitialized]);

  useEffect(() => {
    if (!user) return;
    if (filterInitialized) return;
    setIncludeThirdPartyTickets(user.include_third_party_tickets !== false);
  }, [user?.id, user?.include_third_party_tickets, filterInitialized]);

  // Prioridade P1–P4 só analista/admin; usuário usa urgência (tickets e compras).
  useEffect(() => {
    if (!filterInitialized) return;
    if (!canSeeTicketPriority) {
      if (filterPriority) setFilterPriority('');
      if (sortColumn === 'priority') {
        setSortColumn('created_at');
        setSortDirection('desc');
      }
    }
  }, [filterInitialized, currentArea, canSeeTicketPriority, filterPriority, sortColumn]);

  useEffect(() => {
    if (filterAssignedToPeerId == null) return;
    const peer = myGroupPeerOptions.find((p) => p.user_id === filterAssignedToPeerId);
    if (peer) setAssignedPeerSearch(peer.user_name);
  }, [filterAssignedToPeerId, myGroupPeerOptions]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(DASHBOARD_ITEMS_PER_PAGE_STORAGE_KEY, String(itemsPerPage));
  }, [itemsPerPage]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(TITLE_COLUMN_STORAGE_KEY, String(titleColumnWidth));
  }, [titleColumnWidth]);

  useEffect(() => {
    if (!user?.id || !filterInitialized) return;
    if (filterAssignedToPeerId != null) {
      sessionStorage.setItem(assignedPeerSessionKey(user.id, currentArea), String(filterAssignedToPeerId));
    } else {
      sessionStorage.removeItem(assignedPeerSessionKey(user.id, currentArea));
    }
  }, [filterAssignedToPeerId, user?.id, currentArea, filterInitialized]);

  useEffect(() => {
    if (!filterInitialized) return;
    const payload = {
      filterStatuses,
      filterUrgency,
      filterPriority,
      filterWorkflow,
      filterGroup,
      filterAssignedToPeerId,
      searchText,
      includeThirdPartyTickets,
      showSubtasks,
      filterCreatedByMe,
      closedVisibility,
    };
    sessionStorage.setItem(dashboardFiltersSessionKey(currentArea), JSON.stringify(payload));
  }, [
    filterInitialized,
    currentArea,
    filterStatuses,
    filterUrgency,
    filterPriority,
    filterWorkflow,
    filterGroup,
    filterAssignedToPeerId,
    searchText,
    includeThirdPartyTickets,
    showSubtasks,
    filterCreatedByMe,
    closedVisibility,
  ]);

  // Carregar workflows e grupos assim que o componente montar ou quando a área mudar
  useEffect(() => {
    fetchWorkflows();
    fetchGroups();
    fetchMyGroupPeers();
  }, [currentArea]);

  // Executar fetchData apenas após o filtro ser inicializado (ou se não houver usuário)
  useEffect(() => {
    // Só executar se o filtro foi inicializado OU se não há usuário (para não bloquear)
    if (filterInitialized) {
      fetchData();
    }
  }, [filterStatuses, filterUrgency, filterPriority, filterWorkflow, filterGroup, filterAssignedToPeerId, searchText, includeThirdPartyTickets, showSubtasks, filterCreatedByMe, closedVisibility, currentPage, itemsPerPage, filterInitialized, currentArea, user, canSeeTicketPriority, sortColumn, sortDirection]);

  // Resetar para página 1 quando filtros ou ordenação mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatuses, filterUrgency, filterPriority, filterWorkflow, filterGroup, filterAssignedToPeerId, searchText, includeThirdPartyTickets, showSubtasks, filterCreatedByMe, closedVisibility, sortColumn, sortDirection]);

  // Troca de contexto: limpa lista da área anterior para não misturar tickets/compras na UI.
  useEffect(() => {
    setAllTickets([]);
    setTotalTickets(0);
    setStats({ total: 0, aberto: 0, em_andamento: 0, resolvido: 0, novo: 0, cancelado: 0 });
  }, [currentArea]);

  
  const fetchGroups = async () => {
    try {
      // Lista slim (sem members) — suficiente para filtros/dropdowns
      const response = await axios.get(`${API_URL}/api/groups`, {
        params: { limit: 1000 },
      });
      setGroups(response.data);
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    }
  };

  const fetchMyGroupPeers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/groups/my-peers`);
      setMyGroupPeers(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar colegas de grupo:', error);
      setMyGroupPeers([]);
    }
  };

  const fetchData = async ({ silent = false }: { silent?: boolean } = {}) => {
    try {
      if (silent) setIsRefreshingData(true);
      else setLoading(true);

      // Contexto Compras → só purchases. Contexto Incidentes → feed de incidentes
      // (tickets + subtarefas de compra via dashboard-items). Não chama o outro módulo.
      const apiEndpoint =
        currentArea === 'purchases' ? '/api/purchases/' : '/api/tickets/dashboard-items';
      const summaryEndpoint =
        currentArea === 'purchases'
          ? '/api/purchases/dashboard-summary'
          : '/api/tickets/dashboard-items-summary';
      const parentField = currentArea === 'purchases' ? 'parent_request_id' : 'parent_ticket_id';

      const filterParams: any = {};
      const selectedStatuses = statusesForArea(currentArea, filterStatuses);
      if (selectedStatuses.length > 0) filterParams.statuses = selectedStatuses;
      if (filterUrgency) filterParams.urgency = filterUrgency;
      if (filterPriority && canSeeTicketPriority) {
        filterParams.priority = filterPriority;
      }
      if (filterWorkflow) filterParams.workflow_id = filterWorkflow;
      if (filterGroup) filterParams.assigned_group_id = parseInt(filterGroup);
      {
        let assigneeId: number | undefined;
        if (filterAssignedToPeerId != null) assigneeId = filterAssignedToPeerId;
        if (assigneeId != null) filterParams.assigned_to = assigneeId;
      }
      if (searchText) filterParams.search = searchText;
      if (filterCreatedByMe) filterParams.created_by_me = true;
      if (closedVisibility !== DEFAULT_CLOSED_VISIBILITY) {
        filterParams.closed_visibility = closedVisibility;
      }
      if (showSubtasks) {
        filterParams.include_subtasks = true;
      } else {
        filterParams[parentField] = 0;
        filterParams.include_subtasks = false;
      }
      if (currentArea === 'tickets' || currentArea === 'purchases') {
        filterParams.include_third_party_tickets =
          user ? includeThirdPartyTickets : true;
      }
      // Alinha sort do servidor com /tickets (creator → created_by).
      const apiSortBy = sortColumn === 'creator' ? 'created_by' : sortColumn;
      const skip = (currentPage - 1) * itemsPerPage;
      const params: any = {
        limit: itemsPerPage,
        skip: skip,
        ...filterParams,
        sort_by: apiSortBy,
        sort_order: sortDirection,
      };

      const requestConfig = {
        params,
        paramsSerializer: { serialize: serializeDashboardParams },
      };
      const summaryConfig = {
        params: filterParams,
        paramsSerializer: { serialize: serializeDashboardParams },
      };

      const [ticketsResponse, summaryResponse] = await Promise.all([
        axios.get(`${API_URL}${apiEndpoint}`, requestConfig),
        axios.get(`${API_URL}${summaryEndpoint}`, summaryConfig),
      ]);

      const tickets = ticketsResponse.data as DashboardItem[];
      const summary = summaryResponse.data as {
        total: number;
        counts_by_status: Record<string, number>;
        hidden_concluded_total?: number;
      };

      setAllTickets(tickets);
      setTotalTickets(summary.total);
      setHiddenConcludedTotal(summary.hidden_concluded_total || 0);
      setStats(statsFromCountsByStatus(summary.total, summary.counts_by_status || {}));
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      if (silent) setIsRefreshingData(false);
      else setLoading(false);
    }
  };

  const fetchWorkflows = async () => {
    try {
      // Para criação, buscar apenas workflows publicados filtrados por área
      const areaType = currentArea === 'purchases' ? 'purchases' : 'tickets';
      const response = await axios.get(`${API_URL}/api/workflows/published`, {
        params: { area_type: areaType }
      });
      let loadedWorkflows = (response.data || []).filter(
        (workflow: Workflow) => String(workflow?.name || '').trim() !== '__OPPORTUNITIES_MODULE__'
      );

      // Fallback local: alguns ambientes possuem fluxos ativos não publicados.
      if (loadedWorkflows.length === 0) {
        const fallbackResponse = await axios.get(`${API_URL}/api/workflows`, {
          params: { area_type: areaType, only_valid: false, limit: 1000 }
        });
        loadedWorkflows = (fallbackResponse.data || []).filter(
          (workflow: Workflow) =>
            workflow?.is_active &&
            String(workflow?.name || '').trim() !== '__OPPORTUNITIES_MODULE__'
        );
      }

      setWorkflows(loadedWorkflows);
    } catch (error) {
      console.error('Erro ao carregar fluxos:', error);
    }
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Se já está ordenando por esta coluna, inverter a direção
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Se é uma nova coluna, começar com descendente (mais recente primeiro)
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return (
        <span className="sort-icon ms-2" aria-hidden="true">
          ↕
        </span>
      );
    }
    return (
      <span className="sort-icon active ms-2" aria-hidden="true">
        {sortDirection === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  const startTitleResize = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startWidth = titleColumnWidth;
    const onMove = (moveEvent: MouseEvent) => {
      setTitleColumnWidth(clampTitleColumnWidth(startWidth + (moveEvent.clientX - startX)));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const titleColumnStyle = {
    width: titleColumnWidth,
    minWidth: titleColumnWidth,
    maxWidth: titleColumnWidth,
  };

  const handleThirdPartyFilterChange = async (checked: boolean) => {
    const thirdPartyEntityLabel = currentArea === 'purchases' ? 'requisições' : 'chamados';
    const previous = includeThirdPartyTickets;
    setIncludeThirdPartyTickets(checked);
    try {
      await axios.put(
        `${API_URL}/api/auth/me/include-third-party-tickets?include_third_party_tickets=${checked}`
      );
      await refreshUser();
    } catch (error) {
      setIncludeThirdPartyTickets(previous);
      console.error('Erro ao salvar preferência de terceiros:', error);
      showAlert({
        title: 'Aviso',
        message: `Não foi possível salvar o padrão de visualização de ${thirdPartyEntityLabel} de terceiros.`,
        variant: 'warning'
      });
    }
  };

  // Preferência salva do usuário: só vira chip quando a visualização atual foge dela.
  const thirdPartyDefault = user ? user.include_third_party_tickets !== false : true;

  const handleClearFilters = () => {
    setFilterWorkflow('');
    handleGroupFilterChange('');
    setSearchText('');
    setSearchInputText('');
    setFilterStatuses([]);
    setFilterUrgency('');
    setFilterPriority('');
    setShowSubtasks(false);
    setFilterCreatedByMe(false);
    setClosedVisibility(DEFAULT_CLOSED_VISIBILITY);
    clearAssignedPeer();
    if (user) {
      setIncludeThirdPartyTickets(thirdPartyDefault);
    }
    sessionStorage.removeItem(dashboardFiltersSessionKey(currentArea));
  };

  const activeFilters: ActiveFilter[] = [];
  {
    const selectedWorkflow = workflows.find((workflow: Workflow) => String(workflow.id) === filterWorkflow);
    const selectedGroup = groups.find((group: Group) => String(group.id) === filterGroup);

    if (searchText.trim()) {
      activeFilters.push({
        id: 'search',
        label: `Pesquisa: "${searchText.trim()}"`,
        onRemove: () => {
          setSearchText('');
          setSearchInputText('');
        },
      });
    }
    if (filterWorkflow) {
      activeFilters.push({
        id: 'workflow',
        label: `Fluxo: ${selectedWorkflow?.name || `#${filterWorkflow}`}`,
        onRemove: () => setFilterWorkflow(''),
      });
    }
    if (filterGroup) {
      activeFilters.push({
        id: 'group',
        label: `Grupo: ${selectedGroup?.name || `#${filterGroup}`}`,
        onRemove: () => handleGroupFilterChange(''),
      });
    }
    if (filterAssignedToPeerId != null) {
      const peer = myGroupPeerOptions.find((p) => p.user_id === filterAssignedToPeerId);
      activeFilters.push({
        id: 'assigned-peer',
        label: `Atribuído a: ${peer?.user_name || `#${filterAssignedToPeerId}`}`,
        onRemove: () => clearAssignedPeer(),
      });
    }
    statusesForArea(currentArea, filterStatuses).forEach((statusValue) => {
      activeFilters.push({
        id: `status-${statusValue}`,
        label: `Status: ${getStatusLabel(statusValue)}`,
        onRemove: () => setFilterStatuses((current) => current.filter((item) => item !== statusValue)),
      });
    });
    if (filterUrgency) {
      activeFilters.push({
        id: 'urgency',
        label: `Urgência: ${getUrgencyLabel(filterUrgency)}`,
        onRemove: () => setFilterUrgency(''),
      });
    }
    if (filterPriority && canSeeTicketPriority) {
      activeFilters.push({
        id: 'priority',
        label: `Prioridade: ${getPriorityLabel(filterPriority)}`,
        onRemove: () => setFilterPriority(''),
      });
    }
    const closedVisibilityChip = closedVisibilityChipLabel(closedVisibility);
    if (closedVisibilityChip) {
      activeFilters.push({
        id: 'closed-visibility',
        label: closedVisibilityChip,
        onRemove: () => setClosedVisibility(DEFAULT_CLOSED_VISIBILITY),
      });
    }
    if (user && includeThirdPartyTickets !== thirdPartyDefault) {
      activeFilters.push({
        id: 'third-party',
        label: `Terceiros: ${includeThirdPartyTickets ? 'Incluídos' : 'Ocultos'}`,
        onRemove: () => handleThirdPartyFilterChange(thirdPartyDefault),
      });
    }
    if (showSubtasks) {
      activeFilters.push({
        id: 'subtasks',
        label: 'Subtarefas: Incluídas',
        onRemove: () => setShowSubtasks(false),
      });
    }
    if (filterCreatedByMe) {
      activeFilters.push({
        id: 'created-by-me',
        label: 'Apenas itens criados por mim',
        onRemove: () => setFilterCreatedByMe(false),
      });
    }
  }

  const closedVisibilityMessage = useMemo(
    () =>
      closedVisibilityNotice(
        closedVisibility,
        hiddenConcludedTotal,
        currentArea === 'purchases'
          ? { singular: 'requisição', plural: 'requisições' }
          : { singular: 'chamado', plural: 'chamados' }
      ),
    [closedVisibility, hiddenConcludedTotal, currentArea]
  );

  const {
    isRefreshing: isAutoRefreshing,
    secondsSinceRefresh,
    refreshNow,
  } = useAutoRefresh({
    refreshFn: () => fetchData({ silent: true }),
    enabled: filterInitialized,
    intervalMs: 30000,
  });

  const refreshStatusLabel = useMemo(() => {
    if (isAutoRefreshing || isRefreshingData) return 'Atualizando...';
    if (secondsSinceRefresh == null) return 'Aguardando primeira atualização automática';
    if (secondsSinceRefresh < 60) return `Atualizado há ${secondsSinceRefresh}s`;
    const minutes = Math.floor(secondsSinceRefresh / 60);
    return `Atualizado há ${minutes}min`;
  }, [isAutoRefreshing, isRefreshingData, secondsSinceRefresh]);

  const isPurchaseSubtaskInIncidentArea = (item: DashboardItem): item is PurchaseRequest =>
    currentArea === 'tickets' && (item as PurchaseRequest).item_source === 'purchase_subtask';

  const getItemDisplayId = (item: DashboardItem): string => {
    if (currentArea === 'purchases' || isPurchaseSubtaskInIncidentArea(item)) {
      return getPurchaseDisplayId(item as PurchaseRequest);
    }
    return String(item.id);
  };

  const getItemDetailPath = (item: DashboardItem): string => {
    if (currentArea === 'purchases' || isPurchaseSubtaskInIncidentArea(item)) {
      return `/purchases/${item.id}`;
    }
    return `/tickets/${item.id}`;
  };

  const getItemTypeLabel = (item: DashboardItem): string => {
    if (currentArea === 'purchases') {
      const isSubtask = Boolean(
        item && (
          (item as PurchaseRequest).parent_request_id ||
          (item as PurchaseRequest).parent_purchase_request_id
        )
      );
      if (!isSubtask) return 'Requisição';
      return (item as PurchaseRequest).is_individual_subtask ? 'Subtarefa individual' : 'Subtarefa';
    }
    if (isPurchaseSubtaskInIncidentArea(item)) {
      return (item as PurchaseRequest).is_individual_subtask
        ? 'Subtarefa individual de Compra'
        : 'Subtarefa de Compra';
    }
    return (item as Ticket).parent_ticket_id ? 'Subtarefa' : 'Ticket';
  };

  // Atalho de descoberta: quem recebe uma subtarefa individual não sabe que precisa
  // ligar o filtro de subtarefas para encontrá-la.
  const isMySubtasksViewActive =
    showSubtasks && !!user && filterAssignedToPeerId === user.id;

  const toggleMySubtasksView = () => {
    if (!user) return;
    if (isMySubtasksViewActive) {
      setShowSubtasks(false);
      clearAssignedPeer();
      return;
    }
    setShowSubtasks(true);
    setFilterAssignedToPeerId(user.id);
    setAssignedPeerSearch(user.name);
  };

  const selectAssignedPeer = (peer: AssigneePeerOption) => {
    setFilterAssignedToPeerId(peer.user_id);
    setAssignedPeerSearch(peer.user_name);
  };

  const clearAssignedPeer = () => {
    setFilterAssignedToPeerId(null);
    setAssignedPeerSearch('');
  };

  const applySearchFilter = () => {
    setSearchText(searchInputText.trim());
  };

  const handleGroupFilterChange = async (groupId: string) => {
    setFilterGroup(groupId);
    
    // Salvar preferência no backend
    try {
      const groupIdNum = groupId ? parseInt(groupId) : null;
      const url = groupIdNum 
        ? `${API_URL}/api/auth/me/dashboard-group-filter?group_id=${groupIdNum}`
        : `${API_URL}/api/auth/me/dashboard-group-filter`;
      await axios.put(url);
      
      // Atualizar dados do usuário para refletir a mudança
      const userResponse = await axios.get(`${API_URL}/api/auth/me`);
      // O AuthContext não expõe setUser diretamente, então vamos apenas garantir que está salvo
      // O próximo login ou refresh da página carregará o filtro correto
    } catch (error) {
      console.error('Erro ao salvar filtro de grupo:', error);
      // Continuar mesmo se falhar, pois já atualizou o estado local
    }
  };

  const handleCreateTicket = async (title: string, description: string, priority: string, workflowId?: number, groupId?: number) => {
    if (!workflowId) {
      showAlert({
        title: 'Atenção',
        message: `Por favor, selecione um ${currentArea === 'purchases' ? 'tipo de contratação' : 'fluxo de trabalho'} antes de criar ${currentArea === 'purchases' ? 'a requisição' : 'o ticket'}.`,
        variant: 'warning'
      });
      return;
    }
    
    if (!groupId) {
      showAlert({
        title: 'Atenção',
        message: `Por favor, selecione um grupo para encaminhar ${currentArea === 'purchases' ? 'a requisição' : 'o chamado'}.`,
        variant: 'warning'
      });
      return;
    }
    
    try {
      const apiEndpoint = currentArea === 'purchases' ? '/api/purchases' : '/api/tickets';
      await axios.post(`${API_URL}${apiEndpoint}`, {
        title,
        description,
        priority,
        workflow_id: workflowId,
        assigned_group_id: groupId,
      });
      
      fetchData();
      setShowCreateModal(false);
      showAlert({
        title: 'Sucesso',
        message: `${currentArea === 'purchases' ? 'Requisição' : 'Ticket'} criado com sucesso!`,
        variant: 'success'
      });
    } catch (error: any) {
      console.error(`Erro ao criar ${currentArea === 'purchases' ? 'requisição' : 'ticket'}:`, error);
      showAlert({
        title: `Erro ao Criar ${currentArea === 'purchases' ? 'Requisição' : 'Ticket'}`,
        message: `Não foi possível criar ${currentArea === 'purchases' ? 'a requisição' : 'o ticket'}.`,
        variant: 'danger',
        details: (() => {
          if (error.response?.data?.detail) {
            const detail = error.response.data.detail;
            if (Array.isArray(detail)) {
              return detail.map((d: any) => `${d.loc?.join('.') || ''}: ${d.msg || ''}`).join('\n');
            } else if (typeof detail === 'object') {
              return JSON.stringify(detail);
            } else {
              return String(detail);
            }
          }
          return 'Erro desconhecido';
        })()
      });
    }
  };

  if (loading) {
    return <div className="loading-spinner">Carregando...</div>;
  }

  return (
    <>
      <AlertComponent />
      <div className="dashboard-page">
      <div className="dashboard-hero mb-3">
        <div className="d-flex justify-content-between align-items-center dashboard-header">
          <div>
            <h2 className="dashboard-title mb-1">Dashboard {currentArea === 'purchases' ? '- Compras' : ''}</h2>
            <small className="text-muted">
              Painel operacional com atualização automática sem recarregar a página.
            </small>
          </div>
          <div className="d-flex gap-2 flex-wrap justify-content-end">
            <Button size="sm" variant="outline-secondary" onClick={() => void refreshNow()} disabled={isAutoRefreshing || loading}>
              Atualizar agora
            </Button>
            <Button size="sm" variant="primary" onClick={() => navigate(currentArea === 'purchases' ? '/purchases' : '/tickets')}>
              {currentArea === 'purchases' ? 'Consultar Requisições' : 'Consultar Tickets'}
            </Button>
            <Button size="sm" variant="success" onClick={() => setShowCreateModal(true)}>
              {currentArea === 'purchases' ? '+ Nova Requisição' : '+ Novo Ticket'}
            </Button>
          </div>
        </div>
        <div className="dashboard-live-meta">
          <span className="dashboard-live-dot" />
          <small>{refreshStatusLabel}</small>
        </div>
      </div>

      <Row className="mb-3 dashboard-stats-row g-2">
        <Col xs={6} lg={3}>
          <Card className="text-center dashboard-stat-card dashboard-section-card h-100">
            <Card.Body>
              <Card.Title className="dashboard-stat-title">Total {currentArea === 'purchases' ? 'de Requisições' : 'de Tickets'}</Card.Title>
              <h2 className="text-primary dashboard-stat-value">{stats.total}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} lg={3}>
          <Card className="text-center dashboard-stat-card dashboard-section-card h-100">
            <Card.Body>
              <Card.Title className="dashboard-stat-title">Abertos</Card.Title>
              <h2 className="text-warning dashboard-stat-value">{stats.aberto}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} lg={3}>
          <Card className="text-center dashboard-stat-card dashboard-section-card h-100">
            <Card.Body>
              <Card.Title className="dashboard-stat-title">Em Andamento</Card.Title>
              <h2 className="text-info dashboard-stat-value">{stats.em_andamento}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} lg={3}>
          <Card className="text-center dashboard-stat-card dashboard-section-card h-100">
            <Card.Body>
              <Card.Title className="dashboard-stat-title">Resolvidos</Card.Title>
              <h2 className="text-success dashboard-stat-value">{stats.resolvido}</h2>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <FilterPanel
        panelId="dashboard-filters-panel"
        activeFilters={activeFilters}
        expanded={showFilters}
        onToggleExpanded={() => setShowFilters((prev) => !prev)}
        onClearAll={handleClearFilters}
        notice={closedVisibilityMessage}
      >
        <FilterSection title="Busca e escopo">
          <FilterField label="Pesquisa" controlId="dashboard-filter-search" wide>
            <FilterInputGroup>
              <FilterControl
                id="dashboard-filter-search"
                type="text"
                placeholder="Título ou descrição; para ID use #número"
                value={searchInputText}
                onChange={(e) => setSearchInputText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applySearchFilter();
                  }
                }}
              />
              <Button variant="outline-secondary" onClick={applySearchFilter} title="Aplicar busca">
                Buscar
              </Button>
            </FilterInputGroup>
          </FilterField>
          <FilterField label="Fluxo" controlId="dashboard-filter-workflow">
            <FilterDropdownSelect
              id="dashboard-filter-workflow"
              value={filterWorkflow}
              onChange={setFilterWorkflow}
              options={workflowFilterOptions}
              placeholderLabel="Todos"
            />
          </FilterField>
          <FilterField label="Grupo" controlId="dashboard-filter-group">
            <FilterDropdownSelect
              id="dashboard-filter-group"
              value={filterGroup}
              onChange={handleGroupFilterChange}
              options={groupFilterOptions}
              placeholderLabel="Todos"
            />
          </FilterField>
          <FilterField label="Membro do grupo" controlId="dashboard-filter-peer">
            <FilterAssigneeCombobox
              id="dashboard-filter-peer"
              peerOptions={myGroupPeerOptions}
              filteredPeers={filteredPeerSuggestions}
              searchText={assignedPeerSearch}
              onSearchTextChange={(value) => {
                setAssignedPeerSearch(value);
                setFilterAssignedToPeerId(null);
              }}
              selectedPeerId={filterAssignedToPeerId}
              onSelectPeer={(peer: AssigneePeerOption) => selectAssignedPeer(peer)}
              onClearPeer={clearAssignedPeer}
            />
          </FilterField>
        </FilterSection>

        <FilterSection title="Refinar resultados">
          <FilterField
            label="Status"
            controlId="dashboard-filter-status"
            hint="Segure Ctrl (Cmd no Mac) e clique para combinar status. Sem seleção: todos."
          >
            <StatusMultiFilter
              idPrefix="dashboard-filter-status"
              area={currentArea}
              selected={statusesForArea(currentArea, filterStatuses)}
              onChange={setFilterStatuses}
            />
          </FilterField>
          {(currentArea === 'tickets' || currentArea === 'purchases') && (
            <FilterField label="Urgência" controlId="dashboard-filter-urgency">
              <FilterDropdownSelect
                id="dashboard-filter-urgency"
                value={filterUrgency}
                onChange={setFilterUrgency}
                options={URGENCY_OPTIONS}
                placeholderLabel="Todas"
              />
            </FilterField>
          )}
          {canSeeTicketPriority && (
            <FilterField label="Prioridade" controlId="dashboard-filter-priority">
              <FilterDropdownSelect
                id="dashboard-filter-priority"
                value={filterPriority}
                onChange={setFilterPriority}
                options={priorityFilterOptions}
                placeholderLabel="Todas"
              />
            </FilterField>
          )}
          <FilterField
            label="Conclusão"
            controlId="dashboard-filter-closed-visibility"
            hint={
              currentArea === 'purchases'
                ? 'Vale para requisições fechadas e canceladas, pela data de conclusão.'
                : 'Vale para chamados fechados e cancelados, pela data de conclusão.'
            }
          >
            <FilterDropdownSelect
              id="dashboard-filter-closed-visibility"
              value={closedVisibility}
              onChange={(value) => setClosedVisibility(normalizeClosedVisibility(value))}
              options={closedVisibilityFilterOptions}
              showEmptyOption={false}
            />
          </FilterField>
        </FilterSection>

        <FilterSection title="Exibição" layout="inline">
          {user && (
            <FilterToggle
              id="dashboard-filter-third-party"
              label={
                currentArea === 'purchases'
                  ? includeThirdPartyTickets
                    ? 'Incluir requisições de terceiros'
                    : 'Ocultar requisições de terceiros'
                  : includeThirdPartyTickets
                    ? 'Incluir chamados de terceiros'
                    : 'Ocultar chamados de terceiros'
              }
              checked={includeThirdPartyTickets}
              onChange={handleThirdPartyFilterChange}
            />
          )}
          <FilterToggle
            id="dashboard-filter-subtasks"
            label={showSubtasks ? 'Incluir subtarefas' : 'Ocultar subtarefas'}
            checked={showSubtasks}
            onChange={setShowSubtasks}
          />
          <FilterToggle
            id="dashboard-filter-created-by-me"
            label={filterCreatedByMe ? 'Apenas itens criados por mim' : 'Mostrar itens de todos os autores'}
            checked={filterCreatedByMe}
            onChange={setFilterCreatedByMe}
          />
        </FilterSection>
      </FilterPanel>

      <Card className="dashboard-section-card">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div className="d-flex flex-column">
            <span>{currentArea === 'purchases' ? 'Todas as Requisições' : 'Todos os Tickets'} ({totalTickets})</span>
          </div>
          <div className="d-flex align-items-center gap-2">
            {(isAutoRefreshing || isRefreshingData) && <small className="text-muted">Atualizando dados...</small>}
            <Form.Select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              style={{ width: 'auto' }}
              size="sm"
            >
              <option value={5}>5 por página</option>
              <option value={10}>10 por página</option>
              <option value={20}>20 por página</option>
              <option value={50}>50 por página</option>
              <option value={100}>100 por página</option>
            </Form.Select>
          </div>
        </Card.Header>
        <Card.Body>
          {sortedTickets.length === 0 ? (
            <div className="empty-state">
              <p>Nenhum {currentArea === 'purchases' ? 'requisição' : 'ticket'} encontrado</p>
            </div>
          ) : (
            <>
              <Table responsive hover className="dashboard-table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>
                      <div 
                        className={`sortable-header d-flex align-items-center ${sortColumn === 'id' ? 'active' : ''}`}
                        onClick={() => handleSort('id')}
                      >
                        ID
                        {getSortIcon('id')}
                      </div>
                    </th>
                    <TitleColumnHeader
                      width={titleColumnWidth}
                      sortActive={sortColumn === 'title'}
                      sortIcon={getSortIcon('title')}
                      onSort={() => handleSort('title')}
                      onResizeStart={startTitleResize}
                    />
                    <th>Tipo</th>
                    <th>
                      <div 
                        className={`sortable-header d-flex align-items-center ${sortColumn === 'status' ? 'active' : ''}`}
                        onClick={() => handleSort('status')}
                      >
                        Status
                        {getSortIcon('status')}
                      </div>
                    </th>
                    <th>
                      <div 
                        className={`sortable-header d-flex align-items-center ${sortColumn === 'urgency' ? 'active' : ''}`}
                        onClick={() => handleSort('urgency')}
                      >
                        Urgência
                        {getSortIcon('urgency')}
                      </div>
                    </th>
                    <th>Analista</th>
                    {canSeeTicketPriority && (
                    <th>
                      <div 
                        className={`sortable-header d-flex align-items-center ${sortColumn === 'priority' ? 'active' : ''}`}
                        onClick={() => handleSort('priority')}
                      >
                        Prioridade
                        {getSortIcon('priority')}
                      </div>
                    </th>
                    )}
                    <th>
                      <div 
                        className={`sortable-header d-flex align-items-center ${sortColumn === 'creator' ? 'active' : ''}`}
                        onClick={() => handleSort('creator')}
                      >
                        Criado por
                        {getSortIcon('creator')}
                      </div>
                    </th>
                    <th>
                      <div 
                        className={`sortable-header d-flex align-items-center ${sortColumn === 'created_at' ? 'active' : ''}`}
                        onClick={() => handleSort('created_at')}
                      >
                        Data
                        {getSortIcon('created_at')}
                      </div>
                    </th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTickets.map((item: Ticket | PurchaseRequest) => (
                    <tr key={item.id}>
                      <td>#{getItemDisplayId(item)}</td>
                      <td className="dashboard-title-cell" title={item.title} style={titleColumnStyle}>
                        {item.title}
                      </td>
                      <td>
                        <Badge bg={getItemTypeLabel(item) === 'Subtarefa' ? 'info' : 'secondary'} className="dashboard-badge">
                          {getItemTypeLabel(item)}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={getStatusColor(item.status)} className="dashboard-badge">
                          {getStatusLabel(item.status)}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={getUrgencyColor((item as Ticket).urgency)} className="dashboard-badge">
                          {getUrgencyLabel((item as Ticket).urgency)}
                        </Badge>
                      </td>
                      <td
                        className="dashboard-title-cell"
                        title={item.assignee?.name || 'Não atribuído'}
                      >
                        {item.assignee?.name || 'Não atribuído'}
                      </td>
                      {canSeeTicketPriority && (
                        <td>
                          <Badge bg={getPriorityColor(item.priority)} className="dashboard-badge">
                            {getPriorityLabel(item.priority)}
                          </Badge>
                        </td>
                      )}
                      <td className="dashboard-title-cell" title={item.creator?.name || 'N/A'}>{item.creator?.name || 'N/A'}</td>
                      <td>{new Date(item.created_at).toLocaleDateString('pt-BR')}</td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() =>
                            navigate(getItemDetailPath(item), { state: { from: '/dashboard' } })
                          }
                        >
                          Ver
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              
              {/* Paginação */}
              {totalTickets > itemsPerPage && (() => {
                const totalPages = Math.ceil(totalTickets / itemsPerPage);
                const startItem = ((currentPage - 1) * itemsPerPage) + 1;
                const endItem = Math.min(currentPage * itemsPerPage, totalTickets);
                
                // Calcular quais páginas mostrar
                const getPageNumbers = (): number[] => {
                  const pages: number[] = [];
                  const maxVisible = 5;
                  
                  if (totalPages <= maxVisible) {
                    // Mostrar todas as páginas
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    // Mostrar páginas ao redor da página atual
                    let start = Math.max(1, currentPage - 2);
                    let end = Math.min(totalPages, start + maxVisible - 1);
                    
                    // Ajustar início se estiver muito próximo do fim
                    if (end - start < maxVisible - 1) {
                      start = Math.max(1, end - maxVisible + 1);
                    }
                    
                    for (let i = start; i <= end; i++) {
                      pages.push(i);
                    }
                  }
                  
                  return pages;
                };
                
                return (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div>
                      <small className="text-muted">
                        Mostrando {startItem} a {endItem} de {totalTickets} {currentArea === 'purchases' ? 'requisições' : 'tickets'}
                      </small>
                    </div>
                    <Pagination>
                      <Pagination.First 
                        onClick={() => setCurrentPage(1)} 
                        disabled={currentPage === 1}
                      />
                      <Pagination.Prev 
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} 
                        disabled={currentPage === 1}
                      />
                      
                      {getPageNumbers().map((pageNum: number) => (
                        <Pagination.Item
                          key={pageNum}
                          active={pageNum === currentPage}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </Pagination.Item>
                      ))}
                      
                      <Pagination.Next 
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} 
                        disabled={currentPage >= totalPages}
                      />
                      <Pagination.Last 
                        onClick={() => setCurrentPage(totalPages)} 
                        disabled={currentPage >= totalPages}
                      />
                    </Pagination>
                  </div>
                );
              })()}
            </>
          )}
        </Card.Body>
      </Card>

      <TicketCreateModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        workflows={workflows}
        groups={groups}
        showAlert={showAlert}
        onSuccess={() => {
          fetchData();
          setShowCreateModal(false);
        }}
        areaType={currentArea === 'purchases' ? 'purchases' : 'tickets'}
      />
      </div>
    </>
  );
};

const PURCHASE_OBJECT_HELP_TEXT = 'Descreva, de maneira sucinta, o serviço ou material a ser contratado.';
const PURCHASE_ITEMS_HELP_TEXT = `Descreva de maneira detalhada os itens que compõem o objeto.

Se MATERIAL, cada item deverá ser detalhado apresentando a descrição/identificação completa.

Se SERVIÇO, todo detalhamento deverá estar incluso no memorial descritivo, exceto se aditivo prazo.
Independentemente da natureza, faz-se necessário a inclusão das seguintes informações: número serial, modelo, norma balizadora do item, quantidade, prazo de garantia, local e prazo de entrega ou execução dos serviços, suporte, entre outros.

Para mais de um item requisitado, todos necessariamente deverão ser relacionados aos mesmos fornecedores indicados posteriormente.`;
const PURCHASE_WORKFLOW_HELP_TEXT = `Selecione conforme instruções:

ADITIVO para alterações contratuais.

CANCELAMENTO/ENCERRAMENTO DE CONTRATO Deverá ser utilizado quando for necessário encerrar um contrato com o fornecedor antes do fim da vigência contratual ou quando se deseja cancelar um contrato em andamento.

CONTRATAÇÃO DIRETA se efetuada em regime de exceção para contratações de característica restrita.

CONTRATAÇÃO EMERGENCIAL para contratações com escopo que envolvam mitigação de riscos com prejuízos à imagem da empresa.

INOVAÇÃO Deverá ser utilizado quando a demanda for de contratação de inovação.

RENOVAÇÃO (Contratação Direta) contratação com nova relação contratual a ser firmada com o mesmo contratado.

RENOVAÇÃO (Processo Competitivo) contratação do mesmo objeto, por meio de nova relação contratual a ser firmada com o novo fornecedor.

RFI (Requisição de Informação ao Fornecedor) Deverá ser utilizado em situações em que se tem pouco conhecimento sobre o produto ou serviço a ser adquirido e seus possíveis fornecedores, mas não resulta em uma compra.

RFQ (Requisição de Cotação) Deverá ser utilizado quando se deseja conhecer o preço praticado pelo mercado. Não gera obrigatoriedade de contratação.

SOLICITAÇÃO DE COTAÇÃO para regime de contratação através da solicitação de propostas no mercado.`;

interface TicketCreateModalProps {
  show: boolean;
  onHide: () => void;
  workflows: Workflow[];
  groups: Group[];
  onSuccess: () => void;
  showAlert: (options: { title: string; message: string; variant?: 'success' | 'danger' | 'warning' | 'info'; details?: string }) => void;
  areaType?: 'tickets' | 'purchases';
}

const TicketCreateModal: React.FC<TicketCreateModalProps> = ({
  show,
  onHide,
  workflows,
  groups,
  onSuccess,
  showAlert,
  areaType = 'tickets',
}) => {
  const [title, setTitle] = useState('');
  const [object, setObject] = useState('');
  const [itemsDescription, setItemsDescription] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('p3');
  const [urgency, setUrgency] = useState<TicketUrgency | ''>('');
  const [workflowId, setWorkflowId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [loading, setLoading] = useState(false);
  const [availableGroups, setAvailableGroups] = useState<Group[]>([]);
  const [customFields, setCustomFields] = useState<WorkflowCustomField[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<{ [fieldId: number]: any }>({});
  const [customFieldInlineErrors, setCustomFieldInlineErrors] = useState<{ [fieldId: number]: string }>({});
  const [customFieldFiles, setCustomFieldFiles] = useState<{ [fieldId: number]: File | File[] | null }>({});
  const [customFieldSavedFiles, setCustomFieldSavedFiles] = useState<{ [fieldId: number]: SavedCustomFieldFile }>({});
  const [users, setUsers] = useState<User[]>([]);
  const [analystUsers, setAnalystUsers] = useState<User[]>([]);
  const [searchUsers, setSearchUsers] = useState<{ [fieldId: number]: string }>({});
  const [filteredUsers, setFilteredUsers] = useState<{ [fieldId: number]: User[] }>({});
  /** Compras: resultados da busca /api/users/purchase-picker por campo customizado tipo usuário */
  const [purchaseUserPickerResults, setPurchaseUserPickerResults] = useState<{ [fieldId: number]: User[] }>({});
  const [pickedPurchaseCustomUsers, setPickedPurchaseCustomUsers] = useState<{ [fieldId: number]: User }>({});
  /** Menu de sugestões com position:fixed para não ser cortado pelo overflow do Modal.Body */
  const [customFieldSuggestAnchor, setCustomFieldSuggestAnchor] = useState<{
    [fieldId: number]: { top: number; left: number; width: number };
  }>({});
  const purchaseUserPickerTimersRef = useRef<{ [fieldId: number]: ReturnType<typeof setTimeout> }>({});
  const [requesterId, setRequesterId] = useState<number | null>(null);
  const [requesterSearchTerm, setRequesterSearchTerm] = useState<string>('');
  const [debouncedRequesterQuery, setDebouncedRequesterQuery] = useState<string>('');
  const [filteredRequesterUsers, setFilteredRequesterUsers] = useState<User[]>([]);
  const [requesterSearchLoading, setRequesterSearchLoading] = useState(false);
  const [selectedRequester, setSelectedRequester] = useState<User | null>(null);
  const [documentInstances, setDocumentInstances] = useState<DocumentInstance[]>([]);
  const [documentInstancesByField, setDocumentInstancesByField] = useState<{ [fieldId: number]: DocumentInstance[] }>({});
  const [platformDocumentMode, setPlatformDocumentMode] = useState<{ [fieldId: number]: 'upload' | 'platform' | 'create' }>({});
  const [platformDocumentSelectedIds, setPlatformDocumentSelectedIds] = useState<{ [fieldId: number]: number | null }>({});
  const [draftId, setDraftId] = useState<number | null>(null);
  const [isDraft, setIsDraft] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const isSavingDraftRef = useRef(false);
  const pendingDraftSaveRef = useRef(false);
  const stopDraftAutosaveRef = useRef(false);
  const saveDraftTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const saveDraftToDatabaseRef = useRef<() => Promise<void>>(async () => {});
  const [draftSavedMessage, setDraftSavedMessage] = useState<string | null>(null);
  const [showDraftListModal, setShowDraftListModal] = useState(false);
  const [showDraftConfirmModal, setShowDraftConfirmModal] = useState(false);
  const [draftConfirmData, setDraftConfirmData] = useState<{ title?: string; onConfirm: () => void; onCancel: () => void } | null>(null);
  const [activeTab, setActiveTab] = useState('informacoes');
  const [templateOptions, setTemplateOptions] = useState<TicketTemplate[]>([]);
  const [templateCategory, setTemplateCategory] = useState('');
  const [templateCategories, setTemplateCategories] = useState<string[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [templateSearchTerm, setTemplateSearchTerm] = useState('');
  const [templateFilterCategory, setTemplateFilterCategory] = useState('');
  const [deletingTemplateId, setDeletingTemplateId] = useState<number | null>(null);
  const [purchaseTabKeys, setPurchaseTabKeys] = useState<string[]>([]);
  const [purchaseActiveTab, setPurchaseActiveTab] = useState<string>('');
  const purchaseCustomFieldsRef = useRef<HTMLDivElement>(null);
  const { user: currentUser } = useAuth();
  const canManagePriority =
    areaType === 'purchases'
      ? canManagePurchasePriority(currentUser)
      : canManageTicketPriority(currentUser);
  const isCommonUser = isEndUser(currentUser);

  const schedulePurchaseUserPickerQuery = useCallback((fieldId: number, rawTerm: string) => {
    const prevT = purchaseUserPickerTimersRef.current[fieldId];
    if (prevT) clearTimeout(prevT);
    const term = rawTerm.trim();
    if (!term) {
      setPurchaseUserPickerResults((p) => ({ ...p, [fieldId]: [] }));
      setCustomFieldSuggestAnchor((p) => {
        const n = { ...p };
        delete n[fieldId];
        return n;
      });
      return;
    }
    purchaseUserPickerTimersRef.current[fieldId] = setTimeout(async () => {
      try {
        const response = await axios.get<User[]>(`${API_URL}/api/users/purchase-picker`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
          params: { search: term, limit: 40 },
        });
        setPurchaseUserPickerResults((p) => ({ ...p, [fieldId]: response.data }));
        const el = document.querySelector(
          `[data-custom-suggest-input="${fieldId}"]`
        ) as HTMLElement | null;
        if (el) {
          const r = el.getBoundingClientRect();
          setCustomFieldSuggestAnchor((p) => ({
            ...p,
            [fieldId]: { top: r.bottom + 4, left: r.left, width: Math.max(r.width, 220) },
          }));
        }
      } catch (err) {
        console.error(err);
        setPurchaseUserPickerResults((p) => ({ ...p, [fieldId]: [] }));
      }
    }, 280);
  }, []);

  const initializeCustomFieldValues = useCallback(
    (fields: WorkflowCustomField[], preserveExistingValues: boolean) => {
      setCustomFieldValues((prevValues) => {
        const nextValues: { [fieldId: number]: any } = preserveExistingValues ? { ...prevValues } : {};

        fields.forEach((field) => {
          if (preserveExistingValues && field.id in nextValues) {
            return;
          }

          if (field.field_type === 'list' || field.field_type === 'combo') {
            nextValues[field.id] = field.allow_multiple ? [] : '';
          } else if (field.field_type === 'table') {
            nextValues[field.id] = createDefaultTableValue();
          } else if (field.field_type === 'currency') {
            nextValues[field.id] = JSON.stringify({ currency: 'BRL', value: '' });
          } else if (field.field_type === 'period') {
            nextValues[field.id] = JSON.stringify({ start: '', end: '' });
          } else {
            nextValues[field.id] = '';
          }
        });

        return nextValues;
      });
    },
    []
  );

  const loadCustomFieldsForWorkflow = useCallback(
    async (selectedWorkflowId: string, selectedGroupId?: string, preserveExistingValues = false) => {
      if (!selectedWorkflowId) {
        setCustomFields([]);
        setCustomFieldValues({});
        return;
      }

      try {
        const params = areaType === 'purchases' ? undefined : (selectedGroupId ? { group_id: selectedGroupId } : undefined);
        const fieldsResponse = await axios.get(`${API_URL}/api/workflows/${selectedWorkflowId}/custom-fields/first-step`, {
          params
        });
        const fields = fieldsResponse.data || [];
        setCustomFields(fields);
        initializeCustomFieldValues(fields, preserveExistingValues);
      } catch (error) {
        console.error('Erro ao carregar campos customizados:', error);
        setCustomFields([]);
        if (!preserveExistingValues) {
          setCustomFieldValues({});
        }
      }
    },
    [areaType, initializeCustomFieldValues]
  );

  /** Evita que a troca de workflowId ao hidratar rascunho apague o aceite e demais valores. */
  const preserveDraftHydrationRef = React.useRef(false);
  /** Grupo do rascunho a reaplicar após a lista de grupos carregar (evita race/stale state). */
  const pendingDraftGroupIdRef = React.useRef<string | null>(null);

  // Compras: solicitante sempre o usuário logado
  React.useEffect(() => {
    if (!show || areaType !== 'purchases' || !currentUser) return;
    setRequesterId(currentUser.id);
    setSelectedRequester(currentUser);
    setRequesterSearchTerm('');
    setDebouncedRequesterQuery('');
    setFilteredRequesterUsers([]);
  }, [show, areaType, currentUser]);

  // Carregar rascunho ao abrir modal
  useEffect(() => {
    if (show && areaType === 'purchases' && currentUser) {
      const loadDraft = async () => {
        try {
          // Primeiro, verificar localStorage
          const localDraftKey = `purchase_draft_${currentUser.id}`;
          const localDraft = localStorage.getItem(localDraftKey);
          
          if (localDraft) {
            try {
              const draftData = JSON.parse(localDraft);
              // Verificar se o rascunho é recente (menos de 7 dias)
              const updatedAt = new Date(draftData.updatedAt);
              const daysDiff = (new Date().getTime() - updatedAt.getTime()) / (1000 * 60 * 60 * 24);
              
              if (daysDiff < 7) {
                // Mostrar modal de confirmação amigável
                setDraftConfirmData({
                  title: areaType === 'purchases' ? (draftData.object || draftData.title || undefined) : (draftData.title || undefined),
                  onConfirm: async () => {
                    // Carregar dados do localStorage
                    setCustomFieldSavedFiles({});
                    setCustomFieldFiles({});
                    if (areaType === 'purchases') {
                      setObject(draftData.object || draftData.title || '');
                    } else {
                      setTitle(draftData.title || '');
                      setObject(draftData.object || '');
                    }
                    setItemsDescription(draftData.itemsDescription || '');
                    setDescription(draftData.description || '');
                    setPriority(draftData.priority || 'p4');
                    setUrgency((draftData.urgency as TicketUrgency) || '');
                    const workflowIdStr = draftData.workflowId || '';
                    const groupIdStr = draftData.groupId || '';
                    preserveDraftHydrationRef.current = true;
                    pendingDraftGroupIdRef.current = groupIdStr || null;
                    setWorkflowId(workflowIdStr);
                    setGroupId(groupIdStr);
                    setRequesterId(currentUser.id);
                    setSelectedRequester(currentUser);
                    setRequesterSearchTerm('');
                    setDebouncedRequesterQuery('');
                    setFilteredRequesterUsers([]);
                    if (draftData.customFieldValues) {
                      setCustomFieldValues(draftData.customFieldValues);
                    }
                    setIsDraft(true);
                    
                    // Carregar campos personalizados assim que houver workflow
                    if (workflowIdStr) {
                      await loadCustomFieldsForWorkflow(workflowIdStr, groupIdStr, true);
                    }
                    
                    // Tentar buscar rascunho do banco também
                    try {
                      const response = await axios.get(`${API_URL}/api/purchases/drafts`);
                      if (response.data && response.data.length > 0) {
                        const latestDraft = response.data[0];
                        setDraftId(latestDraft.id);
                        // Isolar anexos: zerar antes e hidratar só deste draftId
                        setCustomFieldSavedFiles({});
                        setCustomFieldFiles({});
                        // Carregar campos personalizados do banco
                        if (latestDraft.id) {
                          const customFieldsResponse = await axios.get(`${API_URL}/api/purchases/${latestDraft.id}/custom-fields`);
                          if (customFieldsResponse.data) {
                            const customValues: { [fieldId: number]: any } = {};
                            const platformModes: { [fieldId: number]: 'upload' | 'platform' | 'create' } = {};
                            const platformDocIds: { [fieldId: number]: number | null } = {};
                            const savedFiles: { [fieldId: number]: SavedCustomFieldFile } = {};
                            customFieldsResponse.data.forEach((cf: any) => {
                              // Verificar se é campo platform_document com document_instance_id
                              if (cf.document_instance_id) {
                                platformModes[cf.custom_field_id] = 'platform';
                                platformDocIds[cf.custom_field_id] = cf.document_instance_id;
                                customValues[cf.custom_field_id] = cf.document_instance_id.toString();
                              } else if (cf.file_path || cf.file_paths) {
                                const parsedSaved = parseSavedCustomFieldFilesFromApi(cf);
                                if (parsedSaved) savedFiles[cf.custom_field_id] = parsedSaved;
                              } else if (cf.text_value != null && String(cf.text_value) !== '') {
                                customValues[cf.custom_field_id] = hydrateCustomFieldTextValue(String(cf.text_value));
                              }
                            });
                            setCustomFieldValues(prev => ({ ...prev, ...customValues }));
                            setCustomFieldSavedFiles(savedFiles);
                            setCustomFieldFiles({});
                            setPlatformDocumentMode(prev => ({ ...prev, ...platformModes }));
                            setPlatformDocumentSelectedIds(prev => ({ ...prev, ...platformDocIds }));
                          }
                        }
                      }
                    } catch (error) {
                      console.error('Erro ao buscar rascunho do banco:', error);
                    }
                    setShowDraftConfirmModal(false);
                    setDraftConfirmData(null);
                  },
                  onCancel: () => {
                    // Limpar rascunho do localStorage se usuário não quiser continuar
                    localStorage.removeItem(localDraftKey);
                    // Limpar todos os campos do formulário
                    if (areaType !== 'purchases') {
                      setTitle('');
                    }
                    setObject('');
                    setItemsDescription('');
                    setDescription('');
                    setPriority('p3');
                    setUrgency('');
                    setWorkflowId('');
                    setGroupId('');
                    setActiveTab('informacoes');
                    setRequesterId(null);
                    setCustomFieldValues({});
                    setCustomFieldSavedFiles({});
                    setCustomFieldFiles({});
                    setDraftId(null);
                    setIsDraft(false);
                    setShowDraftConfirmModal(false);
                    setDraftConfirmData(null);
                  }
                });
                setShowDraftConfirmModal(true);
              } else {
                // Rascunho muito antigo, remover
                localStorage.removeItem(localDraftKey);
              }
            } catch (error) {
              console.error('Erro ao carregar rascunho do localStorage:', error);
              localStorage.removeItem(localDraftKey);
            }
          } else {
            // Se não houver no localStorage, tentar buscar do banco
            try {
              const response = await axios.get(`${API_URL}/api/purchases/drafts`);
              if (response.data && response.data.length > 0) {
                const latestDraft = response.data[0];
                // Mostrar modal de confirmação amigável
                setDraftConfirmData({
                  title: areaType === 'purchases' ? (latestDraft.object || latestDraft.title || undefined) : (latestDraft.title || undefined),
                  onConfirm: async () => {
                    setDraftId(latestDraft.id);
                    setIsDraft(true);
                    setCustomFieldSavedFiles({});
                    setCustomFieldFiles({});
                    if (areaType === 'purchases') {
                      // Usar object, ou title se object não existir (compatibilidade com rascunhos antigos)
                      setObject(latestDraft.object || latestDraft.title || '');
                    } else {
                      setTitle(latestDraft.title || '');
                      setObject(latestDraft.object || '');
                    }
                    setItemsDescription(latestDraft.items_description || '');
                    setDescription(latestDraft.description || '');
                    setPriority(latestDraft.priority || 'p4');
                    setUrgency((latestDraft.urgency as TicketUrgency) || '');
                    const workflowIdStr = latestDraft.workflow_id ? String(latestDraft.workflow_id) : '';
                    const groupIdStr = latestDraft.assigned_group_id ? String(latestDraft.assigned_group_id) : '';
                    preserveDraftHydrationRef.current = true;
                    pendingDraftGroupIdRef.current = groupIdStr || null;
                    setWorkflowId(workflowIdStr);
                    setGroupId(groupIdStr);
                    setRequesterId(currentUser.id);
                    setSelectedRequester(currentUser);
                    setRequesterSearchTerm('');
                    setDebouncedRequesterQuery('');
                    setFilteredRequesterUsers([]);
                    
                    // Carregar campos personalizados assim que houver workflow
                    if (workflowIdStr) {
                      await loadCustomFieldsForWorkflow(workflowIdStr, groupIdStr, true);
                    }
                    
                    // Carregar valores dos campos personalizados salvos no rascunho
                    const customFieldsResponse = await axios.get(`${API_URL}/api/purchases/${latestDraft.id}/custom-fields`);
                    if (customFieldsResponse.data) {
                      const customValues: { [fieldId: number]: any } = {};
                      const platformModes: { [fieldId: number]: 'upload' | 'platform' | 'create' } = {};
                      const platformDocIds: { [fieldId: number]: number | null } = {};
                      const savedFiles: { [fieldId: number]: SavedCustomFieldFile } = {};
                      
                      customFieldsResponse.data.forEach((cf: any) => {
                        // Verificar se é campo platform_document com document_instance_id
                        if (cf.document_instance_id) {
                          platformModes[cf.custom_field_id] = 'platform';
                          platformDocIds[cf.custom_field_id] = cf.document_instance_id;
                          customValues[cf.custom_field_id] = cf.document_instance_id.toString();
                        } else if (cf.file_path || cf.file_paths) {
                          const parsedSaved = parseSavedCustomFieldFilesFromApi(cf);
                          if (parsedSaved) savedFiles[cf.custom_field_id] = parsedSaved;
                        } else if (cf.text_value != null && String(cf.text_value) !== '') {
                          customValues[cf.custom_field_id] = hydrateCustomFieldTextValue(String(cf.text_value));
                        }
                      });
                      setCustomFieldValues(customValues);
                      setCustomFieldSavedFiles(savedFiles);
                      setCustomFieldFiles({});
                      setPlatformDocumentMode(prev => ({ ...prev, ...platformModes }));
                      setPlatformDocumentSelectedIds(prev => ({ ...prev, ...platformDocIds }));
                    }
                    setShowDraftConfirmModal(false);
                    setDraftConfirmData(null);
                  },
                  onCancel: () => {
                    // Limpar todos os campos do formulário
                    if (areaType !== 'purchases') {
                      setTitle('');
                    }
                    setObject('');
                    setItemsDescription('');
                    setDescription('');
                    setPriority('p3');
                    setUrgency('');
                    setWorkflowId('');
                    setGroupId('');
                    setActiveTab('informacoes');
                    setRequesterId(null);
                    setCustomFieldValues({});
                    setCustomFieldSavedFiles({});
                    setCustomFieldFiles({});
                    setDraftId(null);
                    setIsDraft(false);
                    setShowDraftConfirmModal(false);
                    setDraftConfirmData(null);
                  }
                });
                setShowDraftConfirmModal(true);
              }
            } catch (error) {
              console.error('Erro ao buscar rascunhos do banco:', error);
            }
          }
        } catch (error) {
          console.error('Erro ao carregar rascunho:', error);
        }
      };
      
      loadDraft();
    } else if (!show) {
      // Limpar estados quando modal fecha (inclui anexos do rascunho para não vazar entre aberturas)
      setDraftId(null);
      setIsDraft(false);
      setDraftSavedMessage(null);
      stopDraftAutosaveRef.current = false;
      pendingDraftSaveRef.current = false;
      if (saveDraftTimeoutRef.current) {
        clearTimeout(saveDraftTimeoutRef.current);
        saveDraftTimeoutRef.current = null;
      }
      setCustomFieldSavedFiles({});
      setCustomFieldFiles({});
      preserveDraftHydrationRef.current = false;
      pendingDraftGroupIdRef.current = null;
    }
  }, [show]);

  React.useEffect(() => {
    if (show) return;
    setPurchaseUserPickerResults({});
    setPickedPurchaseCustomUsers({});
    setCustomFieldSuggestAnchor({});
    Object.values(purchaseUserPickerTimersRef.current).forEach((t) => clearTimeout(t));
    purchaseUserPickerTimersRef.current = {};
  }, [show]);

  // Compras: exibir nome do usuário selecionado em campo customizado tipo usuário (ex.: rascunho só com id)
  React.useEffect(() => {
    if (areaType !== 'purchases' || !show) return;
    const ac = new AbortController();
    (async () => {
      for (const field of customFields) {
        if (field.field_type !== 'user') continue;
        const sid = Number(customFieldValues[field.id]);
        if (!Number.isFinite(sid) || sid < 1) continue;
        try {
          const r = await axios.get<User>(`${API_URL}/api/users/${sid}`, {
            headers: { Authorization: `Bearer ${getAuthToken()}` },
            signal: ac.signal,
          });
          setPickedPurchaseCustomUsers((prev) =>
            prev[field.id]?.id === r.data.id ? prev : { ...prev, [field.id]: r.data }
          );
        } catch {
          /* ignore */
        }
      }
    })();
    return () => ac.abort();
  }, [areaType, show, customFields, customFieldValues]);

  // Refs para rastrear mudanças e distinguir entre mudança de workflow vs grupo
  const prevWorkflowIdRef = React.useRef<string>('');
  const prevGroupIdRef = React.useRef<string>('');

  // Carregar grupos baseado no workflow selecionado (apenas quando workflow muda)
  React.useEffect(() => {
    const loadGroupsForWorkflow = async () => {
      if (workflowId) {
        try {
          let nextAvailableGroups: Group[] = [];
          const selectedWorkflow = workflows.find((w: Workflow) => String(w.id) === workflowId);
          if (selectedWorkflow && selectedWorkflow.groups) {
            // Se o workflow já tem grupos carregados, usar eles
            nextAvailableGroups = selectedWorkflow.groups.filter((g: Group) => g.is_active);
          } else {
            // Caso contrário, buscar do endpoint
            const response = await axios.get(`${API_URL}/api/groups`, {
              params: { workflow_id: parseInt(workflowId) }
            });
            nextAvailableGroups = response.data.filter((g: Group) => g.is_active);
          }
          // Preferir grupo pendente do rascunho (estado groupId pode estar stale neste efeito)
          const pendingGroupId = pendingDraftGroupIdRef.current;
          const candidateGroupId = pendingGroupId !== null ? pendingGroupId : groupId;
          const preservedGroupId = nextAvailableGroups.some((g: Group) => String(g.id) === candidateGroupId)
            ? candidateGroupId
            : '';
          setAvailableGroups(nextAvailableGroups);
          setGroupId(preservedGroupId);
          prevGroupIdRef.current = preservedGroupId;
          if (pendingGroupId !== null) {
            pendingDraftGroupIdRef.current = null;
          }
          const wasPreservingDraft = preserveDraftHydrationRef.current;
          if (!wasPreservingDraft) {
            setCustomFieldFiles({});
          }
          setActiveTab('informacoes');
          const preserveValues =
            wasPreservingDraft ||
            (areaType === 'purchases' ? false : !!preservedGroupId);
          await loadCustomFieldsForWorkflow(
            workflowId,
            areaType === 'purchases' ? undefined : preservedGroupId,
            preserveValues
          );
          if (wasPreservingDraft) {
            preserveDraftHydrationRef.current = false;
          }
        } catch (error) {
          console.error('Erro ao carregar grupos do workflow:', error);
          setAvailableGroups([]);
          if (pendingDraftGroupIdRef.current !== null) {
            setGroupId(pendingDraftGroupIdRef.current);
            pendingDraftGroupIdRef.current = null;
          }
          if (!preserveDraftHydrationRef.current) {
            setCustomFields([]);
            setCustomFieldValues({});
          } else {
            preserveDraftHydrationRef.current = false;
          }
        }
      } else {
        setAvailableGroups([]);
        setGroupId('');
        prevGroupIdRef.current = '';
        pendingDraftGroupIdRef.current = null;
        preserveDraftHydrationRef.current = false;
        setCustomFields([]);
        setCustomFieldValues({});
        setCustomFieldFiles({});
        setActiveTab('informacoes');
      }
    };

    // Só recarregar se o workflow mudou
    const workflowChanged = prevWorkflowIdRef.current !== workflowId;
    if (workflowChanged) {
      prevWorkflowIdRef.current = workflowId;
      loadGroupsForWorkflow();
    }
  }, [areaType, loadCustomFieldsForWorkflow, workflowId, workflows]);

  // Quando apenas o grupo muda (workflow permanece o mesmo), preservar valores dos campos personalizados
  React.useEffect(() => {
    // Só executar se:
    // 1. Temos um workflowId válido
    // 2. O workflow não mudou (já foi tratado no useEffect anterior)
    // 3. O grupo realmente mudou
    if (!workflowId || prevWorkflowIdRef.current !== workflowId) {
      prevGroupIdRef.current = groupId;
      return;
    }

    // Se o grupo mudou mas o workflow é o mesmo, recarregar campos preservando valores
    const groupChanged = prevGroupIdRef.current !== groupId;
    if (groupChanged && groupId) {
      prevGroupIdRef.current = groupId;
      loadCustomFieldsForWorkflow(
        workflowId,
        areaType === 'purchases' ? undefined : groupId,
        true // preserveExistingValues = true para manter valores já preenchidos
      );
    }
  }, [groupId, workflowId, areaType, loadCustomFieldsForWorkflow]);

  // Aprovadores (compras). Em tickets o solicitante usa /api/users/search ao digitar; lista completa só para campos customizados tipo usuário
  React.useEffect(() => {
    const fetchUsers = async () => {
      try {
        if (areaType === 'purchases') {
          const response = await axios.get(`${API_URL}/api/users/approvers`, {
            headers: { Authorization: `Bearer ${getAuthToken()}` },
            params: { limit: 1000 }
          });
          setUsers(response.data);
        } else {
          setUsers([]);
        }
      } catch (error) {
        console.error('Erro ao buscar usuários:', error);
        if (areaType === 'tickets') setUsers([]);
      }
    };

    if (areaType === 'tickets' || areaType === 'purchases') {
      fetchUsers();
    }
  }, [areaType]);

  React.useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedRequesterQuery(requesterSearchTerm);
    }, 350);
    return () => window.clearTimeout(t);
  }, [requesterSearchTerm]);

  React.useEffect(() => {
    if (areaType !== 'tickets' && areaType !== 'purchases') return;
    const q = debouncedRequesterQuery.trim();
    if (q.length < 1) {
      setFilteredRequesterUsers([]);
      setRequesterSearchLoading(false);
      return;
    }
    const ac = new AbortController();
    setRequesterSearchLoading(true);
    (async () => {
      try {
        const token = getAuthToken();
        const items =
          areaType === 'purchases'
            ? await searchPurchaseFormUsers(q, token, { limit: 40, signal: ac.signal })
            : await searchActiveUsers(q, token, { limit: 25, signal: ac.signal });
        setFilteredRequesterUsers(items);
      } catch (err: unknown) {
        if (axios.isAxiosError(err) && err.code === 'ERR_CANCELED') return;
        setFilteredRequesterUsers([]);
      } finally {
        setRequesterSearchLoading(false);
      }
    })();
    return () => ac.abort();
  }, [debouncedRequesterQuery, areaType]);

  const refreshTemplateData = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const [categories, templates] = await Promise.all([
        listTicketTemplateCategories(),
        listTicketTemplates(),
      ]);
      setTemplateCategories(categories);
      setTemplateOptions(templates);
    } catch (error) {
      console.error('Erro ao carregar templates de chamados:', error);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  React.useEffect(() => {
    if (!show || areaType !== 'tickets') return;
    refreshTemplateData();
  }, [show, areaType, refreshTemplateData]);

  // Exibir nome do solicitante ao restaurar rascunho (só id salvo)
  React.useEffect(() => {
    if (areaType !== 'tickets' && areaType !== 'purchases') return;
    if (!requesterId) {
      setSelectedRequester(null);
      return;
    }
    if (selectedRequester?.id === requesterId) return;
    const ac = new AbortController();
    (async () => {
      try {
        const r = await axios.get<User>(`${API_URL}/api/users/${requesterId}`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
          signal: ac.signal,
        });
        setSelectedRequester(r.data);
      } catch {
        setRequesterId(null);
        setSelectedRequester(null);
      }
    })();
    return () => ac.abort();
  }, [areaType, requesterId, selectedRequester?.id]);

  // Carregar usuários e analistas quando necessário
  React.useEffect(() => {
    const hasUserField = customFields.some(f => f.field_type === 'user');
    const hasAnalystField = customFields.some(f => f.field_type === 'analyst');
    const hasPlatformDocumentField = customFields.some(f => f.field_type === 'platform_document');

    const fetchData = async () => {
      if (hasUserField && areaType !== 'purchases') {
        try {
          const response = await axios.get(`${API_URL}/api/users`, {
            headers: { Authorization: `Bearer ${getAuthToken()}` }
          });
          setUsers(response.data.filter((u: User) => u.is_active));
        } catch (error) {
          console.error('Erro ao buscar usuários:', error);
        }
      }

      if (hasAnalystField && groupId) {
        try {
          const response = await axios.get(`${API_URL}/api/groups/${groupId}`);
          const groupMembers = response.data.members || [];
          setAnalystUsers(groupMembers.map((m: any) => ({
            id: m.user_id,
            name: m.user_name,
            email: m.user_email,
            is_active: true
          })));
        } catch (error) {
          console.error('Erro ao buscar analistas do grupo:', error);
        }
      }

      if (hasPlatformDocumentField && areaType === 'purchases') {
        // Buscar documentos para cada campo platform_document com seu tipo específico
        const documentPromises = customFields
          .filter(f => f.field_type === 'platform_document')
          .map(async (field) => {
            try {
              const params: any = { status: 'finalized' };
              if (field.document_type_id) {
                params.document_type_id = field.document_type_id;
              }
              const response = await axios.get(`${API_URL}/api/documents/instances/available`, {
                params,
                headers: { Authorization: `Bearer ${getAuthToken()}` }
              });
              return { fieldId: field.id, documents: response.data };
            } catch (error) {
              console.error(`Erro ao buscar documentos para campo ${field.id}:`, error);
              return { fieldId: field.id, documents: [] };
            }
          });
        
        const results = await Promise.all(documentPromises);
        // Criar um mapa de fieldId -> documentos
        const documentsByField: { [fieldId: number]: DocumentInstance[] } = {};
        results.forEach(result => {
          documentsByField[result.fieldId] = result.documents;
        });
        setDocumentInstancesByField(documentsByField);
        // Para compatibilidade, manter documentInstances com todos os documentos (sem filtro)
        const allDocuments = results.flatMap(r => r.documents);
        const uniqueDocuments = Array.from(new Map(allDocuments.map(d => [d.id, d])).values());
        setDocumentInstances(uniqueDocuments);
      }
    };

    if (customFields.length > 0) {
      fetchData();
    }
  }, [customFields, groupId, areaType]);

  /** Campos de data cuja sugestão deve ser reposicionada por causa de uma edição do usuário. */
  const pendingDatePrefillRef = useRef<Set<number>>(new Set());

  const handleCustomFieldChange = (fieldId: number, value: any) => {
    // Editar qualquer campo pode mudar o prazo mínimo de uma data (Orçamento Estimado ou as
    // condições da faixa). Marcamos as datas para reposicionar a sugestão; o efeito de prefill
    // só age de fato se o mínimo calculado mudar.
    customFields.forEach((field) => {
      if (field.field_type === 'date' && field.validation_field_id && field.id !== fieldId) {
        pendingDatePrefillRef.current.add(field.id);
      }
    });
    setCustomFieldValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  const applyTemplate = (template: TicketTemplate) => {
    setTitle(template.title || '');
    setDescription(template.description || '');
    setTemplateCategory(template.category || '');
    if (template.urgency) {
      setUrgency(template.urgency);
    }
    if (areaType === 'purchases' && canManagePriority && template.priority) {
      setPriority(template.priority);
    }
    if (template.workflow_id) {
      setWorkflowId(String(template.workflow_id));
    }
    if (template.assigned_group_id) {
      setGroupId(String(template.assigned_group_id));
    }
    if (template.requester_id) {
      setRequesterId(template.requester_id);
    } else {
      setRequesterId(null);
      setSelectedRequester(null);
      setRequesterSearchTerm('');
      setDebouncedRequesterQuery('');
      setFilteredRequesterUsers([]);
    }
    showAlert({
      title: 'Template aplicado',
      message: 'Campos do template aplicados com sucesso.',
      variant: 'success',
    });
  };

  const handleSaveTemplate = async () => {
    if (savingTemplate) return;

    const normalizedTitle = title.trim();
    const normalizedDescription = description.trim();
    const normalizedCategory = templateCategory.trim();

    if (!normalizedTitle || !normalizedDescription) {
      showAlert({
        title: 'Dados insuficientes',
        message: 'Preencha título e descrição antes de salvar o template.',
        variant: 'warning',
      });
      return;
    }

    setSavingTemplate(true);
    try {
      await createTicketTemplate({
        title: normalizedTitle,
        description: normalizedDescription,
        category: normalizedCategory || null,
        urgency: urgency || null,
        priority:
          areaType === 'purchases' && canManagePriority && !isCommonUser
            ? (priority as 'p1' | 'p2' | 'p3' | 'p4')
            : null,
        requester_id: requesterId || null,
        assigned_group_id: groupId ? parseInt(groupId) : null,
        workflow_id: workflowId ? parseInt(workflowId) : null,
      });
      await refreshTemplateData();
      if (normalizedCategory && !templateCategories.includes(normalizedCategory)) {
        setTemplateCategories((prev) => [...prev, normalizedCategory].sort((a, b) => a.localeCompare(b, 'pt-BR')));
      }
      showAlert({
        title: 'Template salvo',
        message: 'Template de chamado salvo com sucesso.',
        variant: 'success',
      });
      setShowSaveTemplateModal(false);
    } catch (error: any) {
      showAlert({
        title: 'Erro ao salvar template',
        message: 'Não foi possível salvar o template.',
        variant: 'danger',
        details: error?.response?.data?.detail || 'Erro desconhecido',
      });
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (templateId: number) => {
    setDeletingTemplateId(templateId);
    try {
      await deleteTicketTemplate(templateId);
      await refreshTemplateData();
      showAlert({
        title: 'Template removido',
        message: 'Template excluído com sucesso.',
        variant: 'success',
      });
    } catch (error: any) {
      showAlert({
        title: 'Erro ao excluir template',
        message: 'Não foi possível excluir o template selecionado.',
        variant: 'danger',
        details: error?.response?.data?.detail || 'Erro desconhecido',
      });
    } finally {
      setDeletingTemplateId(null);
    }
  };

  const filteredTemplateOptions = useMemo(() => {
    const search = templateSearchTerm.trim().toLowerCase();
    return templateOptions.filter((item) => {
      const byCategory = templateFilterCategory ? item.category === templateFilterCategory : true;
      if (!byCategory) return false;
      if (!search) return true;
      return (
        item.title.toLowerCase().includes(search) ||
        item.description.toLowerCase().includes(search) ||
        (item.category || '').toLowerCase().includes(search)
      );
    });
  }, [templateOptions, templateSearchTerm, templateFilterCategory]);

  // Função para salvar rascunho no localStorage
  const saveDraftToLocalStorage = useCallback(() => {
    if (!currentUser || areaType !== 'purchases') return;
    
    const draftData = {
      title,
      object,
      itemsDescription,
      description,
      priority,
      workflowId,
      groupId,
      customFieldValues,
      requesterId,
      updatedAt: new Date().toISOString()
    };
    
    try {
      localStorage.setItem(`purchase_draft_${currentUser.id}`, JSON.stringify(draftData));
    } catch (error) {
      console.error('Erro ao salvar rascunho no localStorage:', error);
    }
  }, [currentUser, areaType, title, object, itemsDescription, description, priority, workflowId, groupId, customFieldValues, requesterId]);

  // Função para salvar rascunho no banco de dados
  const saveDraftToDatabase = useCallback(async () => {
    if (!currentUser || areaType !== 'purchases') return;
    if (stopDraftAutosaveRef.current) {
      pendingDraftSaveRef.current = false;
      return;
    }
    if (isSavingDraftRef.current) {
      pendingDraftSaveRef.current = true;
      return;
    }

    try {
      isSavingDraftRef.current = true;
      setIsSavingDraft(true);

      const hasSignificantData = title || object || itemsDescription || description || workflowId || groupId || Object.keys(customFieldValues).length > 0;
      if (!draftId && !hasSignificantData) {
        return;
      }

      const saved = await savePurchaseDraftRecord(
        API_URL,
        draftId,
        buildPurchaseDraftPayload({
          title,
          object,
          itemsDescription,
          description,
          urgency,
          priority,
          includePriority: canManagePriority,
          workflowId,
          groupId,
          requesterId: currentUser?.id ?? null,
        }),
        { recreateIfNotDraft: false }
      );
      if (saved.skipped || !saved.id) {
        return;
      }
      const savedDraftId = saved.id;
      if (saved.created || !draftId) {
        setDraftId(savedDraftId);
        setIsDraft(true);
      }

      // Salvar campos personalizados (POST + PUT se já existir — sem GET inexistente)
      if (savedDraftId && customFields.length > 0) {
        const customFieldPromises = customFields
          .filter(field => {
            const fieldValue = customFieldValues[field.id];
            const mode = platformDocumentMode[field.id] || 'upload';
            // Incluir platform_document apenas se estiver no modo 'platform' com documento selecionado
            if (field.field_type === 'platform_document') {
              return mode === 'platform' && fieldValue !== undefined && fieldValue !== '' && fieldValue !== null;
            }
            // Campos de arquivo não são salvos aqui (são salvos separadamente via upload)
            if (field.field_type === 'file' || field.field_type === 'files') {
              return false;
            }
            if (field.field_type === 'check' && !field.allow_multiple) {
              return hasCheckFieldPersistedValue(field, fieldValue);
            }
            return fieldValue !== undefined && fieldValue !== null && fieldValue !== '' &&
                   !(Array.isArray(fieldValue) && fieldValue.length === 0);
          })
          .map(async (field) => {
            const fieldValue = customFieldValues[field.id];
            const valueData: any = {
              purchase_request_id: savedDraftId,
              custom_field_id: field.id,
            };

            // Tratar campo platform_document no modo 'platform'
            if (field.field_type === 'platform_document' && platformDocumentMode[field.id] === 'platform') {
              const docId = platformDocumentSelectedIds[field.id];
              if (docId) {
                valueData.document_instance_id = docId;
                valueData.text_value = null;
                valueData.file_path = null;
                valueData.file_paths = null;
              }
            } else if (field.field_type === 'currency' || field.field_type === 'period') {
              // Campos que são salvos como JSON
              valueData.text_value = typeof fieldValue === 'string' ? fieldValue : JSON.stringify(fieldValue);
            } else if (field.field_type === 'list' || field.field_type === 'combo' || (field.field_type === 'check' && field.allow_multiple)) {
              // Campos de lista/combo/check com múltiplas opções
              valueData.text_value = field.allow_multiple && Array.isArray(fieldValue)
                ? JSON.stringify(fieldValue)
                : String(fieldValue);
            } else if (field.field_type === 'check' && !field.allow_multiple) {
              const checkSerialized = serializeCheckFieldValue(field, fieldValue);
              if (!checkSerialized) {
                return;
              }
              valueData.text_value = checkSerialized;
            } else {
              // Campos de texto, número, data, link, user, analyst, gerente do contrato, etc.
              valueData.text_value = String(fieldValue);
            }

            try {
              await upsertPurchaseCustomFieldValue(API_URL, valueData);
            } catch (error) {
              console.error(`Erro ao salvar campo customizado ${field.id}:`, error);
            }
          });

        await Promise.all(customFieldPromises);
      }

      // Mostrar mensagem de salvamento apenas uma vez por sessão, de forma discreta
      if (!draftSavedMessage) {
        setDraftSavedMessage('Rascunho salvo');
        setTimeout(() => setDraftSavedMessage(null), 2000);
      }
    } catch (error: any) {
      console.error('Erro ao salvar rascunho:', error);
    } finally {
      isSavingDraftRef.current = false;
      setIsSavingDraft(false);
      if (pendingDraftSaveRef.current && !stopDraftAutosaveRef.current) {
        pendingDraftSaveRef.current = false;
        setTimeout(() => {
          void saveDraftToDatabaseRef.current();
        }, 50);
      } else {
        pendingDraftSaveRef.current = false;
      }
    }
  }, [currentUser, areaType, title, object, itemsDescription, description, priority, workflowId, groupId, customFieldValues, customFields, draftId, draftSavedMessage, platformDocumentMode, platformDocumentSelectedIds, urgency, canManagePriority, requesterId]);

  saveDraftToDatabaseRef.current = saveDraftToDatabase;

  const cancelPendingDraftAutosave = useCallback(() => {
    stopDraftAutosaveRef.current = true;
    pendingDraftSaveRef.current = false;
    if (saveDraftTimeoutRef.current) {
      clearTimeout(saveDraftTimeoutRef.current);
      saveDraftTimeoutRef.current = null;
    }
  }, []);

  // Função principal de salvamento (localStorage imediato + banco com debounce)
  const saveDraft = useCallback(() => {
    if (stopDraftAutosaveRef.current) {
      return;
    }
    // Salvar no localStorage imediatamente
    saveDraftToLocalStorage();
    
    // Salvar no banco com debounce de 2 segundos para evitar muitas requisições
    if (saveDraftTimeoutRef.current) {
      clearTimeout(saveDraftTimeoutRef.current);
    }
    
    saveDraftTimeoutRef.current = setTimeout(() => {
      saveDraftToDatabase();
    }, 2000);
  }, [saveDraftToLocalStorage, saveDraftToDatabase]);

  // Observar mudanças nos campos principais e salvar rascunho automaticamente
  // Usar um debounce maior para evitar salvamentos muito frequentes
  // Salvar automaticamente apenas se já houver um draftId (editando rascunho existente)
  useEffect(() => {
    if (areaType === 'purchases' && show && draftId) {
      // Verificar se há algum campo preenchido
      const hasAnyField = title || object || itemsDescription || description || workflowId || groupId;
      if (hasAnyField) {
        // Aguardar 2 segundos antes de salvar para evitar salvamentos muito frequentes
        const timeoutId = setTimeout(() => {
          saveDraft();
        }, 2000);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [title, object, itemsDescription, description, priority, workflowId, groupId, areaType, show, saveDraft, draftId]);

  // Observar mudanças nos campos personalizados e salvar rascunho automaticamente
  // Salvar automaticamente apenas se já houver um draftId (editando rascunho existente)
  useEffect(() => {
    if (areaType === 'purchases' && show && Object.keys(customFieldValues).length > 0 && draftId) {
      // Aguardar 2 segundos antes de salvar para evitar salvamentos muito frequentes
      const timeoutId = setTimeout(() => {
        saveDraft();
      }, 2000);
      return () => clearTimeout(timeoutId);
    }
  }, [customFieldValues, areaType, show, saveDraft, draftId]);

  // Função para formatar valor monetário brasileiro
  const getCurrencySymbol = (currency: string): string => {
    const symbols: { [key: string]: string } = {
      'BRL': 'R$',
      'USD': '$',
      'EUR': '€'
    };
    return symbols[currency] || currency;
  };

  const formatCurrency = (value: string | number, currency: string = 'BRL'): string => {
    if (!value) return '';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '';
    const formatted = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numValue);
    return `${getCurrencySymbol(currency)} ${formatted}`;
  };

  const parseFieldNumericValue = (rawValue: any, fieldType?: string): number =>
    parseCustomFieldNumericValue(rawValue, fieldType);

  const normalizeMultiCheckboxValues = (value: any): string[] => {
    if (Array.isArray(value)) {
      return value.map((v) => String(v).trim());
    }
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed.map((v) => String(v).trim());
        }
        return [String(parsed).trim()];
      } catch {
        return value
          .split(',')
          .map((v) => v.trim())
          .filter((v) => v.length > 0);
      }
    }
    if (value !== undefined && value !== null && value !== '') {
      return [String(value).trim()];
    }
    return [];
  };

  const evaluateConditionSatisfied = (condition: any, values: { [fieldId: number]: any }): boolean => {
    const conditionField = findWorkflowFieldById(customFields, condition.field_id);
    if (!conditionField) return false;

    const conditionValue = values[Number(condition.field_id)];
    const isMultiCheckbox = conditionField.field_type === 'check' && conditionField.allow_multiple;

    if (isMultiCheckbox) {
      const selectedOptions = normalizeMultiCheckboxValues(conditionValue);
      switch (condition.operator) {
        case 'checked':
          return selectedOptions.length > 0;
        case 'unchecked':
          return selectedOptions.length === 0;
        case 'equals': {
          const target = String(condition.value || '').trim();
          return selectedOptions.includes(target);
        }
        case 'not_equals': {
          const target = String(condition.value || '').trim();
          return !selectedOptions.includes(target);
        }
        default:
          return false;
      }
    }

    // Uma única escolha por texto (radio Sim/Não, lista ou combo): não é boolean
    const isSingleChoiceTextField =
      (conditionField.field_type === 'check' && !conditionField.allow_multiple) ||
      conditionField.field_type === 'list' ||
      conditionField.field_type === 'combo';

    if (isSingleChoiceTextField) {
      const sel = String(conditionValue ?? '').trim();
      switch (condition.operator) {
        case 'checked':
          return sel.length > 0;
        case 'unchecked':
          return sel.length === 0;
        case 'equals':
          return sel.toLowerCase() === String(condition.value ?? '').toLowerCase().trim();
        case 'not_equals':
          return sel.toLowerCase() !== String(condition.value ?? '').toLowerCase().trim();
        default:
          return false;
      }
    }

    switch (condition.operator) {
      case 'checked':
        return conditionValue === true || conditionValue === 'true' || conditionValue === 1 || conditionValue === '1';
      case 'unchecked':
        return !(conditionValue === true || conditionValue === 'true' || conditionValue === 1 || conditionValue === '1');
      case 'equals':
        return String(conditionValue).toLowerCase().trim() === String(condition.value).toLowerCase().trim();
      case 'not_equals':
        return String(conditionValue).toLowerCase().trim() !== String(condition.value).toLowerCase().trim();
      default:
        return false;
    }
  };

  const getDateMinValidationForField = (
    field: WorkflowCustomField,
    values: { [fieldId: number]: any }
  ): { minDate: Date; message?: string } | null => {
    if (field.field_type !== 'date' || !field.validation_field_id || !field.validation_rules) {
      return null;
    }

    const validationField = findWorkflowFieldById(customFields, field.validation_field_id);
    const validationFieldValue = values[Number(field.validation_field_id)];
    if (isValidationSourceValueEmpty(validationFieldValue)) {
      return null;
    }

    const numericValidationValue = parseFieldNumericValue(
      validationFieldValue,
      validationField?.field_type
    );
    let bestDays: number | null = null;
    let bestMessage: string | undefined;

    try {
      const parsedRules = parseValidationRulesJson(field.validation_rules);
      if (!parsedRules?.rules?.length) {
        return null;
      }

      parsedRules.rules.forEach((rule: any) => {
        if (rule.operator === 'min_date_days') {
          const d = coerceMinDateDaysValue(rule.min_date_days);
          if (d !== null && (bestDays === null || d > bestDays)) {
            bestDays = d;
            bestMessage = undefined;
          }
          return;
        }

        if (rule.operator !== 'value_ranges' || !Array.isArray(rule.value_ranges) || isNaN(numericValidationValue)) {
          return;
        }

        const picked = pickValueRangeValidationForNumericValue(
          rule.value_ranges,
          numericValidationValue,
          evaluateConditionSatisfied,
          values
        );

        const effectiveValidation = picked?.validation;
        if (effectiveValidation?.type === 'min_date_days') {
          const days = coerceMinDateDaysValue(effectiveValidation.value);
          if (days !== null && (bestDays === null || days > bestDays)) {
            bestDays = days;
            bestMessage = effectiveValidation.message;
          }
        }
      });
    } catch {
      return null;
    }

    if (bestDays === null) {
      return null;
    }

    return { minDate: addDaysLocal(startOfTodayLocal(), bestDays), message: bestMessage };
  };

  /** Prazo mínimo já usado no prefill de cada campo de data. */
  const lastPrefillMinDateRef = useRef<{ [dateFieldId: number]: number }>({});

  // Reposiciona a sugestão (data mínima + 1 dia) sempre que um novo prazo passa a valer.
  // O usuário continua livre para escolher outra data depois, inclusive menor que o mínimo:
  // nesse caso aparece apenas o aviso, sem bloqueio.
  React.useEffect(() => {
    if (customFields.length === 0) return;

    const updates: { [fieldId: number]: any } = {};

    customFields.forEach((field) => {
      if (field.field_type !== 'date' || !field.validation_field_id) {
        return;
      }

      const minValidation = getDateMinValidationForField(field, customFieldValues);
      if (!minValidation) {
        delete lastPrefillMinDateRef.current[field.id];
        return;
      }

      const minDateTime = minValidation.minDate.getTime();
      const prefill = shouldPrefillDateField({
        currentValue: customFieldValues[field.id],
        minDateTime,
        lastPrefillMinDateTime: lastPrefillMinDateRef.current[field.id],
        triggeredByUserEdit: pendingDatePrefillRef.current.has(field.id),
      });
      lastPrefillMinDateRef.current[field.id] = minDateTime;
      pendingDatePrefillRef.current.delete(field.id);

      if (prefill) {
        updates[field.id] = formatDateInputLocal(addDaysLocal(minValidation.minDate, 1));
      }
    });

    if (Object.keys(updates).length > 0) {
      setCustomFieldValues((prev) => ({ ...prev, ...updates }));
    }
  }, [customFields, customFieldValues]);

  // Aviso (não bloqueia o envio) quando a data informada é anterior ao prazo mínimo da faixa.
  React.useEffect(() => {
    if (customFields.length === 0) return;

    const newErrors: { [fieldId: number]: string } = {};
    customFields.forEach((field) => {
      if (field.field_type !== 'date') return;

      const currentValue = customFieldValues[field.id];
      if (!currentValue) return;

      const minValidation = getDateMinValidationForField(field, customFieldValues);
      if (!minValidation) return;

      const enteredDate = parseDateInputLocal(currentValue);
      if (!enteredDate) return;

      if (enteredDate < minValidation.minDate) {
        newErrors[field.id] =
          minValidation.message ||
          `O campo "${field.label}" deve ser pelo menos até ${minValidation.minDate.toLocaleDateString('pt-BR')}.`;
      }
    });

    setCustomFieldInlineErrors(newErrors);
  }, [customFields, customFieldValues]);

  // Calcular e atualizar abas de purchases quando campos personalizados mudarem
  React.useEffect(() => {
    if (areaType !== 'purchases' || customFields.length === 0) {
      setPurchaseTabKeys([]);
      setPurchaseActiveTab('');
      return;
    }

    const shouldShowFieldForTabs = (field: WorkflowCustomField): boolean =>
      shouldShowConditionalField(field, customFields, customFieldValues);

    // Agrupar campos por group_name
    const groupedFields: { [key: string]: WorkflowCustomField[] } = {};
    const ungroupedFields: WorkflowCustomField[] = [];

    customFields.forEach(field => {
      const isVisible = field.step_is_visible !== undefined ? field.step_is_visible : true;
      if (!isVisible) return;
      if (!shouldShowFieldForTabs(field)) return;
      
      if (field.group_name) {
        const groupName = field.group_name;
        if (!groupedFields[groupName]) {
          groupedFields[groupName] = [];
        }
        groupedFields[groupName].push(field);
      } else {
        ungroupedFields.push(field);
      }
    });

    const hasGroups = Object.keys(groupedFields).length > 0;
    const hasUngrouped = ungroupedFields.length > 0;
    
    const allTabKeys: string[] = [];
    Object.keys(groupedFields).forEach(key => allTabKeys.push(key));
    if (hasUngrouped) allTabKeys.push('ungrouped');
    
    setPurchaseTabKeys(allTabKeys);
    if (allTabKeys.length > 0 && (!purchaseActiveTab || !allTabKeys.includes(purchaseActiveTab))) {
      setPurchaseActiveTab(allTabKeys[0]);
    }
  }, [areaType, customFields, customFieldValues]);

  const shouldShowField = (field: WorkflowCustomField): boolean =>
    shouldShowConditionalField(field, customFields, customFieldValues);

  // Função para parsear opções
  const parseOptions = (options?: string): string[] => {
    if (!options) return [];
    // Tentar parsear como JSON primeiro
    try {
      const parsed = JSON.parse(options);
      if (Array.isArray(parsed)) return parsed;
    } catch (e) {
      // Se não for JSON, tratar como texto separado por vírgula ou quebra de linha
      return options
        .split(/[,\n]/)
        .map((opt) => opt.trim())
        .filter((opt) => opt.length > 0);
    }
    return [];
  };

  // Função para renderizar campo customizado
  const renderCustomField = (field: WorkflowCustomField) => {
    const fieldValue = customFieldValues[field.id];
    const userSearchTerm = searchUsers[field.id] || '';
    const isSupplierField = isSupplierCustomField(field);
    const selectedWorkflowName = workflows.find((w: Workflow) => String(w.id) === workflowId)?.name;

    const bumpSuggestAnchor = (fieldId: number, el: HTMLElement | null) => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      setCustomFieldSuggestAnchor((p) => ({
        ...p,
        [fieldId]: { top: r.bottom + 4, left: r.left, width: Math.max(r.width, 220) },
      }));
    };

    if (isSupplierField) {
      return (
        <SupplierFieldControl
          value={typeof fieldValue === 'string' ? fieldValue : ''}
          onChange={(nextSerializedValue) => handleCustomFieldChange(field.id, nextSerializedValue)}
          disabled={!!field.step_is_blocked}
          required={field.is_required && !field.step_is_blocked}
          workflowName={selectedWorkflowName}
          fieldOptions={field.options}
        />
      );
    }
    
    switch (field.field_type) {
      case 'text_short':
        return (
          <Form.Control
            type="text"
            value={fieldValue || ''}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            required={field.is_required}
          />
        );

      case 'text_long':
        return (
          <Form.Control
            as="textarea"
            rows={4}
            value={fieldValue || ''}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            required={field.is_required}
          />
        );

      case 'list':
      case 'combo': {
        const options: string[] = field.options 
          ? field.options.split(/[,\n]/).map((opt: string) => opt.trim()).filter((opt: string) => opt.length > 0)
          : [];
        if (field.allow_multiple) {
          const selectedValues: string[] = Array.isArray(fieldValue) 
            ? (fieldValue as string[])
            : [];
          return (
            <div>
              {options.map((option: string, idx: number) => {
                const isChecked = selectedValues.includes(option);
                return (
                  <Form.Check
                    key={idx}
                    type="checkbox"
                    label={option}
                    checked={isChecked}
                    onChange={(e) => {
                      const newValues: string[] = e.target.checked
                        ? [...selectedValues, option]
                        : selectedValues.filter((v: string) => v !== option);
                      handleCustomFieldChange(field.id, newValues);
                    }}
                  />
                );
              })}
            </div>
          );
        } else {
          return (
            <Form.Select
              value={fieldValue || ''}
              onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
              required={field.is_required}
            >
              <option value="">Selecione...</option>
              {options.map((option: string, idx: number) => (
                <option key={idx} value={option}>
                  {option}
                </option>
              ))}
            </Form.Select>
          );
        }
      }

      case 'date':
        return (
          <div>
            <Form.Control
              type="date"
              value={fieldValue || ''}
              onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
              required={field.is_required}
            />
            {customFieldInlineErrors[field.id] && (
              <Form.Text className="text-warning d-block mt-1">
                {customFieldInlineErrors[field.id]}
              </Form.Text>
            )}
          </div>
        );

      case 'period': {
        let periodValue = { start: '', end: '' };
        if (fieldValue) {
          try {
            periodValue = typeof fieldValue === 'string' ? JSON.parse(fieldValue) : fieldValue;
          } catch {
            periodValue = { start: fieldValue || '', end: '' };
          }
        }
        return (
          <div className="d-flex gap-2">
            <Form.Control
              type="date"
              placeholder="Data Início"
              value={periodValue.start || ''}
              onChange={(e) => {
                const newPeriod = { ...periodValue, start: e.target.value };
                handleCustomFieldChange(field.id, JSON.stringify(newPeriod));
              }}
              required={field.is_required}
            />
            <Form.Control
              type="date"
              placeholder="Data Fim"
              value={periodValue.end || ''}
              onChange={(e) => {
                const newPeriod = { ...periodValue, end: e.target.value };
                handleCustomFieldChange(field.id, JSON.stringify(newPeriod));
              }}
              required={field.is_required}
            />
          </div>
        );
      }

      case 'link':
        return (
          <Form.Control
            type="url"
            value={fieldValue || ''}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            required={field.is_required}
            placeholder="https://example.com"
          />
        );

      case 'user': {
        const selectedId = parseInt(fieldValue || '0', 10);
        const isPurchase = areaType === 'purchases';
        const remoteList = purchaseUserPickerResults[field.id] || [];
        const filteredUserList = isPurchase
          ? remoteList
          : userSearchTerm
            ? users
                .filter(
                  (u) =>
                    u.name?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                    u.email?.toLowerCase().includes(userSearchTerm.toLowerCase())
                )
                .slice(0, 10)
            : [];
        const selectedUser =
          (isPurchase && pickedPurchaseCustomUsers[field.id]?.id === selectedId
            ? pickedPurchaseCustomUsers[field.id]
            : undefined) ||
          users.find((u) => u.id === selectedId) ||
          (isPurchase ? remoteList.find((u) => u.id === selectedId) : undefined);

        const isInvalidUserInput = !!userSearchTerm.trim() && !selectedUser;
        const suggestAnchor = customFieldSuggestAnchor[field.id];
        const showUserSuggest =
          !!userSearchTerm && !selectedUser && !!suggestAnchor && filteredUserList.length > 0;

        return (
          <div style={{ position: 'relative' }}>
            <Form.Control
              type="text"
              data-custom-suggest-input={field.id}
              value={selectedUser ? `${selectedUser.name} (${selectedUser.email})` : userSearchTerm}
              onChange={(e) => {
                const v = e.target.value;
                setSearchUsers({ ...searchUsers, [field.id]: v });
                bumpSuggestAnchor(field.id, e.currentTarget);
                if (!v) {
                  handleCustomFieldChange(field.id, '');
                  setPurchaseUserPickerResults((p) => ({ ...p, [field.id]: [] }));
                  setPickedPurchaseCustomUsers((p) => {
                    const n = { ...p };
                    delete n[field.id];
                    return n;
                  });
                  setCustomFieldSuggestAnchor((p) => {
                    const n = { ...p };
                    delete n[field.id];
                    return n;
                  });
                  const prevT = purchaseUserPickerTimersRef.current[field.id];
                  if (prevT) clearTimeout(prevT);
                  return;
                }
                if (isPurchase) {
                  schedulePurchaseUserPickerQuery(field.id, v);
                }
              }}
              onFocus={(e) => {
                bumpSuggestAnchor(field.id, e.currentTarget);
                if (!isPurchase && !userSearchTerm) {
                  setFilteredUsers({ ...filteredUsers, [field.id]: users.slice(0, 10) });
                }
              }}
              required={field.is_required}
              isInvalid={isInvalidUserInput}
              placeholder="Digite o nome ou email do usuário"
            />
            {showUserSuggest && suggestAnchor && (
              <div
                style={{
                  position: 'fixed',
                  top: suggestAnchor.top,
                  left: suggestAnchor.left,
                  width: suggestAnchor.width,
                  zIndex: 1080,
                  backgroundColor: 'var(--bs-body-bg)',
                  border: '1px solid var(--bs-border-color)',
                  borderRadius: '0 0 0.375rem 0.375rem',
                  maxHeight: '220px',
                  overflowY: 'auto',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
              >
                {filteredUserList.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => {
                      handleCustomFieldChange(field.id, user.id.toString());
                      setSearchUsers({ ...searchUsers, [field.id]: '' });
                      setFilteredUsers({ ...filteredUsers, [field.id]: [] });
                      setCustomFieldSuggestAnchor((p) => {
                        const n = { ...p };
                        delete n[field.id];
                        return n;
                      });
                      setPurchaseUserPickerResults((p) => ({ ...p, [field.id]: [] }));
                      if (isPurchase) {
                        setPickedPurchaseCustomUsers((p) => ({ ...p, [field.id]: user }));
                      }
                    }}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid var(--bs-border-color)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--bs-tertiary-bg)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--bs-body-bg)';
                    }}
                  >
                    {user.name} ({user.email})
                  </div>
                ))}
              </div>
            )}
            {isInvalidUserInput && (
              <Form.Text className="text-danger d-block mt-1">
                Selecione um usuário cadastrado na lista.
              </Form.Text>
            )}
          </div>
        );
      }

      case 'approver':
      case 'contract_manager_approver': {
        const approverSearchTerm = searchUsers[field.id] || '';
        const approverUsers = users.filter(
          (u) =>
            u.is_active &&
            (field.field_type === 'contract_manager_approver' || !!u.can_approve)
        );
        const filteredApproverList = approverSearchTerm
          ? approverUsers.filter(
              (u) =>
                u.name?.toLowerCase().includes(approverSearchTerm.toLowerCase()) ||
                u.email?.toLowerCase().includes(approverSearchTerm.toLowerCase())
            ).slice(0, 10)
          : [];
        // Preferir lista filtrada; se o id salvo não estiver nela (ex.: gerente ativo sem can_approve), olhar users.
        const selectedApprover =
          approverUsers.find((u) => u.id === parseInt(fieldValue || '0', 10)) ||
          users.find((u) => u.id === parseInt(fieldValue || '0', 10));
        const suggestAnchorApp = customFieldSuggestAnchor[field.id];
        const showApproverSuggest =
          !!approverSearchTerm &&
          !selectedApprover &&
          !!suggestAnchorApp &&
          filteredApproverList.length > 0;

        return (
          <div style={{ position: 'relative' }}>
            <Form.Control
              type="text"
              data-custom-suggest-input={field.id}
              value={
                selectedApprover
                  ? `${selectedApprover.name} (${selectedApprover.email})`
                  : fieldValue && !approverSearchTerm
                    ? `Usuário #${fieldValue}`
                    : approverSearchTerm
              }
              onChange={(e) => {
                const v = e.target.value;
                setSearchUsers({ ...searchUsers, [field.id]: v });
                bumpSuggestAnchor(field.id, e.currentTarget);
                if (!v) {
                  handleCustomFieldChange(field.id, '');
                  setCustomFieldSuggestAnchor((p) => {
                    const n = { ...p };
                    delete n[field.id];
                    return n;
                  });
                }
              }}
              onFocus={(e) => {
                bumpSuggestAnchor(field.id, e.currentTarget);
                if (!approverSearchTerm) {
                  setFilteredUsers({ ...filteredUsers, [field.id]: approverUsers.slice(0, 10) });
                }
              }}
              required={field.is_required}
              placeholder="Digite o nome ou email do aprovador"
            />
            {showApproverSuggest && suggestAnchorApp && (
              <div
                style={{
                  position: 'fixed',
                  top: suggestAnchorApp.top,
                  left: suggestAnchorApp.left,
                  width: suggestAnchorApp.width,
                  zIndex: 1080,
                  backgroundColor: 'var(--bs-body-bg)',
                  border: '1px solid var(--bs-border-color)',
                  borderRadius: '0 0 0.375rem 0.375rem',
                  maxHeight: '220px',
                  overflowY: 'auto',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
              >
                {filteredApproverList.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => {
                      handleCustomFieldChange(field.id, user.id.toString());
                      setSearchUsers({ ...searchUsers, [field.id]: '' });
                      setFilteredUsers({ ...filteredUsers, [field.id]: [] });
                      setCustomFieldSuggestAnchor((p) => {
                        const n = { ...p };
                        delete n[field.id];
                        return n;
                      });
                    }}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid var(--bs-border-color)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--bs-tertiary-bg)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--bs-body-bg)';
                    }}
                  >
                    {user.name} ({user.email})
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }

      case 'analyst': {
        const analystSearchTerm = searchUsers[field.id] || '';
        const filteredAnalystList = analystSearchTerm
          ? analystUsers.filter(
              (u) =>
                u.name?.toLowerCase().includes(analystSearchTerm.toLowerCase()) ||
                u.email?.toLowerCase().includes(analystSearchTerm.toLowerCase())
            ).slice(0, 10)
          : [];
        const selectedAnalyst = analystUsers.find((u) => u.id === parseInt(fieldValue || '0'));
        const suggestAnchorAn = customFieldSuggestAnchor[field.id];
        const showAnalystSuggest =
          !!analystSearchTerm &&
          !selectedAnalyst &&
          !!suggestAnchorAn &&
          filteredAnalystList.length > 0;

        return (
          <div style={{ position: 'relative' }}>
            {groupId ? (
              <>
                <Form.Control
                  type="text"
                  data-custom-suggest-input={field.id}
                  value={
                    selectedAnalyst
                      ? `${selectedAnalyst.name} (${selectedAnalyst.email})`
                      : analystSearchTerm
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    setSearchUsers({ ...searchUsers, [field.id]: v });
                    bumpSuggestAnchor(field.id, e.currentTarget);
                    if (!v) {
                      handleCustomFieldChange(field.id, '');
                      setCustomFieldSuggestAnchor((p) => {
                        const n = { ...p };
                        delete n[field.id];
                        return n;
                      });
                    }
                  }}
                  onFocus={(e) => {
                    bumpSuggestAnchor(field.id, e.currentTarget);
                    if (!analystSearchTerm) {
                      setFilteredUsers({ ...filteredUsers, [field.id]: analystUsers.slice(0, 10) });
                    }
                  }}
                  required={field.is_required}
                  placeholder="Digite o nome ou email do analista"
                />
                {showAnalystSuggest && suggestAnchorAn && (
                  <div
                    style={{
                      position: 'fixed',
                      top: suggestAnchorAn.top,
                      left: suggestAnchorAn.left,
                      width: suggestAnchorAn.width,
                      zIndex: 1080,
                      backgroundColor: 'var(--bs-body-bg)',
                      border: '1px solid var(--bs-border-color)',
                      borderRadius: '0 0 0.375rem 0.375rem',
                      maxHeight: '220px',
                      overflowY: 'auto',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    }}
                  >
                    {filteredAnalystList.map((analyst) => (
                      <div
                        key={analyst.id}
                        onClick={() => {
                          handleCustomFieldChange(field.id, analyst.id.toString());
                          setSearchUsers({ ...searchUsers, [field.id]: '' });
                          setFilteredUsers({ ...filteredUsers, [field.id]: [] });
                          setCustomFieldSuggestAnchor((p) => {
                            const n = { ...p };
                            delete n[field.id];
                            return n;
                          });
                        }}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid var(--bs-border-color)',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--bs-tertiary-bg)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--bs-body-bg)';
                        }}
                      >
                        {analyst.name} ({analyst.email})
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="text-muted">
                <small>Selecione um grupo primeiro</small>
              </div>
            )}
          </div>
        );
      }

      case 'integer':
        return (
          <Form.Control
            type="number"
            step="1"
            min="0"
            value={fieldValue || ''}
            onChange={(e) => {
              const value = e.target.value;
              // Apenas inteiros não negativos
              if (value === '' || /^\d+$/.test(value)) {
                handleCustomFieldChange(field.id, value);
              }
            }}
            onWheel={(e) => e.currentTarget.blur()}
            required={field.is_required}
            placeholder="0"
          />
        );

      case 'decimal':
        return (
          <Form.Control
            type="number"
            step="0.01"
            min="0"
            value={fieldValue || ''}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '' || (parseFloat(v) >= 0 && !/^-/.test(v))) {
                handleCustomFieldChange(field.id, v);
              }
            }}
            onWheel={(e) => e.currentTarget.blur()}
            required={field.is_required}
            placeholder="0.00"
          />
        );

      case 'currency': {
        let currencyValue = { currency: 'BRL', value: '' };
        if (fieldValue) {
          try {
            currencyValue = typeof fieldValue === 'string' ? JSON.parse(fieldValue) : fieldValue;
          } catch {
            currencyValue = { currency: 'BRL', value: fieldValue || '' };
          }
        }
        return (
          <div className="d-flex gap-2 align-items-center">
            <Form.Select
              value={currencyValue.currency || 'BRL'}
              onChange={(e) => {
                const newCurrency = { ...currencyValue, currency: e.target.value };
                handleCustomFieldChange(field.id, JSON.stringify(newCurrency));
              }}
              className="csp-wave1-width-150"
            >
              <option value="BRL">BRL (R$)</option>
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
            </Form.Select>
            <Form.Control
              type="text"
              inputMode="decimal"
              value={formatCurrencyInputDisplay(
                currencyValue.value || '',
                currencyValue.currency || 'BRL'
              )}
              onChange={(e) => {
                const normalized = normalizeCurrencyInput(e.target.value);
                if (normalized === null) return;
                const newCurrency = { ...currencyValue, value: normalized };
                handleCustomFieldChange(field.id, JSON.stringify(newCurrency));
              }}
              required={field.is_required}
              placeholder="Ex.: 30000,01"
            />
            {currencyValue.value && (
              <Form.Text className="text-muted text-nowrap">
                {formatCurrency(currencyValue.value, currencyValue.currency)}
              </Form.Text>
            )}
          </div>
        );
      }

      case 'table': {
        return (
          <CustomTableField
            value={fieldValue || createDefaultTableValue()}
            onChange={(nextValue) => handleCustomFieldChange(field.id, nextValue)}
            disabled={!!field.step_is_blocked}
          />
        );
      }

      case 'file': {
        const fieldFile = customFieldFiles[field.id];
        return (
          <div>
            <Form.Control
              type="file"
              onChange={(e) => {
                const target = e.target as HTMLInputElement;
                const file = target.files?.[0] || null;
                if (file) {
                  const error = validateAttachmentFile(file);
                  if (error) {
                    showAlert({
                      title: 'Arquivo inválido',
                      message: error,
                      variant: 'warning',
                    });
                    target.value = '';
                    setCustomFieldFiles({ ...customFieldFiles, [field.id]: null });
                    return;
                  }
                }
                setCustomFieldFiles({ ...customFieldFiles, [field.id]: file });
              }}
              accept={ATTACHMENT_ACCEPT}
            />
            {fieldFile ? (
              <small className="text-muted d-block mt-1">
                Arquivo selecionado: {(fieldFile as File).name}
              </small>
            ) : (
              hasSavedCustomFieldFile(customFieldSavedFiles[field.id]) && (
                <small className="text-success d-block mt-1">
                  {savedCustomFieldFileLabel(customFieldSavedFiles[field.id])}
                </small>
              )
            )}
          </div>
        );
      }

      case 'files': {
        const fieldFiles = customFieldFiles[field.id];
        return (
          <div>
            <Form.Control
              type="file"
              multiple
              onChange={(e) => {
                const target = e.target as HTMLInputElement;
                const files = target.files ? Array.from(target.files) : null;
                if (files && files.length > 0) {
                  const error = validateAttachmentFiles(files);
                  if (error) {
                    showAlert({
                      title: 'Arquivo inválido',
                      message: error,
                      variant: 'warning',
                    });
                    target.value = '';
                    setCustomFieldFiles({ ...customFieldFiles, [field.id]: null });
                    return;
                  }
                }
                setCustomFieldFiles({ ...customFieldFiles, [field.id]: files });
              }}
              accept={ATTACHMENT_ACCEPT}
            />
            {fieldFiles && Array.isArray(fieldFiles) ? (
              <small className="text-muted d-block mt-1">
                {fieldFiles.length} arquivo(s) selecionado(s)
              </small>
            ) : (
              hasSavedCustomFieldFile(customFieldSavedFiles[field.id]) && (
                <small className="text-success d-block mt-1">
                  {savedCustomFieldFileLabel(customFieldSavedFiles[field.id])}
                </small>
              )
            )}
          </div>
        );
      }

      case 'check': {
        const options = parseOptions(field.options);
        const hasOptions = options.length > 0;

        if (hasOptions) {
          let selectedValues: string[] = [];
          if (fieldValue) {
            try {
              if (typeof fieldValue === 'string') {
                const parsed = JSON.parse(fieldValue);
                if (Array.isArray(parsed)) {
                  selectedValues = parsed.map((v) => String(v));
                } else if (parsed !== null && parsed !== undefined && String(parsed).trim() !== '') {
                  selectedValues = [String(parsed)];
                }
              } else if (Array.isArray(fieldValue)) {
                selectedValues = fieldValue.map((v) => String(v));
              } else {
                selectedValues = [String(fieldValue)];
              }
            } catch {
              selectedValues = String(fieldValue).trim() ? [String(fieldValue)] : [];
            }
          }

          const selectedValue = selectedValues[0] || '';
          const shouldShowInfoText = field.checkbox_text_trigger_option
            ? (field.allow_multiple
                ? selectedValues.includes(field.checkbox_text_trigger_option)
                : selectedValue === field.checkbox_text_trigger_option)
            : (field.allow_multiple ? selectedValues.length > 0 : !!selectedValue);

          return (
            <div>
              {field.checkbox_link && (
                <div className="mb-2">
                  <a
                    href={field.checkbox_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-primary"
                  >
                    {field.checkbox_link_text || 'Ler Diretrizes'}
                  </a>
                </div>
              )}
              <div>
                {options.map((option, idx) => (
                  <Form.Check
                    key={idx}
                    className="custom-field-check"
                    type={field.allow_multiple ? 'checkbox' : 'radio'}
                    name={field.allow_multiple ? undefined : `field-${field.id}`}
                    label={option}
                    checked={field.allow_multiple ? selectedValues.includes(option) : selectedValue === option}
                    onChange={(e) => {
                      if (field.allow_multiple) {
                        const newValues = e.target.checked
                          ? [...selectedValues, option]
                          : selectedValues.filter((v) => v !== option);
                        handleCustomFieldChange(field.id, newValues);
                        return;
                      }
                      handleCustomFieldChange(field.id, e.target.checked ? option : '');
                    }}
                  />
                ))}
              </div>
              {field.checkbox_text && shouldShowInfoText && (
                <div className="alert alert-info mt-2">
                  {field.checkbox_text}
                </div>
              )}
            </div>
          );
        }

        // Checkbox único (comportamento original)
        const isChecked = fieldValue === 'true' || fieldValue === true || fieldValue === 1 || fieldValue === '1';
        return (
          <div>
            {field.checkbox_link && (
              <div className="mb-2">
                <a
                  href={field.checkbox_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline-primary"
                >
                  {field.checkbox_link_text || 'Ler Diretrizes'}
                </a>
              </div>
            )}
            <Form.Check
              className="custom-field-check"
              type="checkbox"
              label={field.checkbox_label || field.label}
              checked={isChecked}
              onChange={(e) => {
                handleCustomFieldChange(field.id, e.target.checked ? 'true' : 'false');
              }}
            />
            {isChecked && field.checkbox_text && (
              <div className="alert alert-info mt-2">
                {field.checkbox_text}
              </div>
            )}
          </div>
        );
      }

      case 'platform_document': {
        if (areaType !== 'purchases') {
          return <div className="text-muted">Este tipo de campo só está disponível para compras.</div>;
        }

        const currentMode = platformDocumentMode[field.id] || 'upload';
        const selectedDocumentId = platformDocumentSelectedIds[field.id] || null;
        
        return (
          <div>
            <Form.Group className="mb-3">
              <Form.Label>Modo de Seleção</Form.Label>
              <Form.Select
                value={currentMode}
                onChange={(e) => {
                  const newMode = e.target.value as 'upload' | 'platform' | 'create';
                  setPlatformDocumentMode({ ...platformDocumentMode, [field.id]: newMode });
                  // Limpar valores ao trocar de modo
                  if (newMode === 'upload') {
                    handleCustomFieldChange(field.id, '');
                    setPlatformDocumentSelectedIds({ ...platformDocumentSelectedIds, [field.id]: null });
                  } else if (newMode === 'platform') {
                    setCustomFieldFiles({ ...customFieldFiles, [field.id]: null });
                  } else if (newMode === 'create') {
                    setCustomFieldFiles({ ...customFieldFiles, [field.id]: null });
                    setPlatformDocumentSelectedIds({ ...platformDocumentSelectedIds, [field.id]: null });
                  }
                }}
                disabled={field.step_is_blocked}
              >
                <option value="upload">Upload de Arquivo</option>
                <option value="platform">Selecionar Documento da Plataforma</option>
                <option value="create">Criar Novo Documento</option>
              </Form.Select>
            </Form.Group>

            {currentMode === 'upload' ? (
              <div>
                <Form.Control
                  type="file"
                  onChange={(e) => {
                    const target = e.target as HTMLInputElement;
                    const file = target.files?.[0] || null;
                    if (file) {
                      const error = validateAttachmentFile(file);
                      if (error) {
                        showAlert({
                          title: 'Arquivo inválido',
                          message: error,
                          variant: 'warning',
                        });
                        target.value = '';
                        setCustomFieldFiles({ ...customFieldFiles, [field.id]: null });
                        return;
                      }
                    }
                    setCustomFieldFiles({ ...customFieldFiles, [field.id]: file });
                  }}
                  disabled={field.step_is_blocked}
                  accept={ATTACHMENT_ACCEPT}
                />
                {customFieldFiles[field.id] ? (
                  <small className="text-muted d-block mt-1">
                    Arquivo selecionado: {(customFieldFiles[field.id] as File).name}
                  </small>
                ) : (
                  hasSavedCustomFieldFile(customFieldSavedFiles[field.id]) && (
                    <small className="text-success d-block mt-1">
                      {savedCustomFieldFileLabel(customFieldSavedFiles[field.id])}
                    </small>
                  )
                )}
              </div>
            ) : currentMode === 'platform' ? (
              <div>
                <Form.Select
                  value={selectedDocumentId || ''}
                  onChange={(e) => {
                    const docId = e.target.value ? parseInt(e.target.value) : null;
                    setPlatformDocumentSelectedIds({ ...platformDocumentSelectedIds, [field.id]: docId });
                    // Armazenar o ID do documento no campo customizado para uso no submit
                    handleCustomFieldChange(field.id, docId ? docId.toString() : '');
                  }}
                  disabled={field.step_is_blocked}
                  required={field.is_required}
                >
                  <option value="">Selecione um documento...</option>
                  {(field.document_type_id && documentInstancesByField[field.id] 
                    ? documentInstancesByField[field.id] 
                    : documentInstances).map((doc) => (
                    <option key={doc.id} value={doc.id}>
                      {doc.document_type?.name || 'Documento'} {doc.title ? `- ${doc.title}` : ''} ({new Date(doc.created_at).toLocaleDateString('pt-BR')})
                    </option>
                  ))}
                </Form.Select>
                {selectedDocumentId && (
                  <Button
                    variant="link"
                    size="sm"
                    className="mt-2 p-0"
                    onClick={() => window.open(`/documents/${selectedDocumentId}/edit`, '_blank')}
                    disabled={field.step_is_blocked}
                  >
                    Abrir documento em nova aba
                  </Button>
                )}
              </div>
            ) : (
              <div>
                <Button
                  variant="outline-primary"
                  onClick={() => {
                    window.open('/documents', '_blank');
                  }}
                  disabled={field.step_is_blocked}
                >
                  Abrir Tela de Documentos
                </Button>
                <Form.Text className="text-muted d-block mt-2">
                  Uma nova aba será aberta para você criar um novo documento. Após criar e finalizar o documento, volte aqui e selecione-o na opção "Selecionar Documento da Plataforma".
                </Form.Text>
              </div>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  // Função para renderizar um campo completo (com Form.Group)
  const renderField = (field: WorkflowCustomField) => {
    // Usar step_is_required se disponível e não for null, senão usar is_required
    const isRequired = (field.step_is_required !== undefined && field.step_is_required !== null) 
      ? !!field.step_is_required
      : !!field.is_required;
    // Determinar se o campo está bloqueado: usar step_is_blocked se disponível
    const isBlocked = field.step_is_blocked !== undefined ? field.step_is_blocked : false;
    
    return (
      <Form.Group key={field.id} className="mb-3">
        <Form.Label>
          {field.label}
          {isRequired && <span className="text-danger ms-1">*</span>}
        </Form.Label>
        {field.description && (
          <Form.Text className="text-muted d-block mb-2" style={{ whiteSpace: 'pre-line', fontSize: '0.875rem', fontStyle: 'italic' }}>
            {field.description}
          </Form.Text>
        )}
        {(() => {
          // Renderizar o campo usando renderCustomField, mas precisamos ajustar as props
          // Para campos que retornam elementos simples, vamos clonar e adicionar props
          const fieldComponent = renderCustomField(field);
          
          // Se for um elemento React válido, tentar adicionar as props de bloqueio
          if (React.isValidElement(fieldComponent)) {
            const element = fieldComponent as React.ReactElement<any>;
            // Verificar se já tem required/disabled definidos
            const currentRequired = element.props?.required;
            const currentDisabled = element.props?.disabled;
            const currentReadOnly = element.props?.readOnly;
            
            return React.cloneElement(element, {
              required: isRequired && !isBlocked ? (currentRequired !== false) : false,
              disabled: isBlocked || currentDisabled,
              readOnly: isBlocked || currentReadOnly,
            });
          }
          
          return fieldComponent;
        })()}
      </Form.Group>
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Proteção contra duplo clique - retornar se já estiver processando
    if (loading) {
      return;
    }
    
    // Validação de campos obrigatórios com mensagem de campo e aba (item 17 do backlog)
    type ValidationError = { message: string; tabKey: string };
    const errors: ValidationError[] = [];

    const hasAnyCustomGroup = customFields.some((field) => {
      const isVisible = field.step_is_visible !== undefined ? field.step_is_visible : true;
      return isVisible && !!field.group_name?.trim();
    });

    const getCustomFieldTabName = (field: WorkflowCustomField): string => {
      const groupName = field.group_name?.trim();
      if (groupName) return groupName;
      return hasAnyCustomGroup ? 'Geral' : 'Campos Customizados';
    };

    const getRequiredFieldMessage = (fieldName: string, tabName?: string): string =>
      tabName
        ? `Preencha o campo "${fieldName}" na aba "${tabName}"`
        : `Preencha o campo "${fieldName}"`;

    customFields.forEach((field) => {
      if (field.field_type !== 'user') return;

      const isBlocked = field.step_is_blocked !== undefined ? field.step_is_blocked : false;
      const isVisible = field.step_is_visible !== undefined ? field.step_is_visible : true;
      if (isBlocked || !isVisible) return;

      const userSearchTerm = (searchUsers[field.id] || '').trim();
      const selectedUserId = Number(customFieldValues[field.id]);
      const hasSelectedUser =
        Number.isFinite(selectedUserId) &&
        selectedUserId > 0 &&
        (users.some((u) => u.id === selectedUserId) ||
          (areaType === 'purchases' && pickedPurchaseCustomUsers[field.id]?.id === selectedUserId));

      if ((userSearchTerm && !hasSelectedUser) || (!userSearchTerm && customFieldValues[field.id] && !hasSelectedUser)) {
        const tabName = getCustomFieldTabName(field);
        errors.push({
          message: `Selecione um usuário cadastrado para o campo "${field.label?.trim() || field.name?.trim() || `ID ${field.id}`}" na aba "${tabName}"`,
          tabKey: field.group_name || 'geral',
        });
      }
    });

    if (areaType === 'purchases') {
      if (!workflowId) {
        errors.push({ message: getRequiredFieldMessage('Tipo de Contratação'), tabKey: 'informacoes' });
      }
      if (!object || object.trim() === '') {
        errors.push({ message: getRequiredFieldMessage('Objeto'), tabKey: 'informacoes' });
      }
      if (!itemsDescription || itemsDescription.trim() === '') {
        errors.push({ message: getRequiredFieldMessage('Descrição dos Itens'), tabKey: 'informacoes' });
      }
      if (!groupId) {
        errors.push({ message: getRequiredFieldMessage('Grupo para Encaminhar'), tabKey: 'informacoes' });
      }
      if (!urgency) {
        errors.push({ message: getRequiredFieldMessage('Urgência', 'Informações'), tabKey: 'informacoes' });
      }
    } else {
      // tickets
      if (!workflowId) {
        errors.push({ message: getRequiredFieldMessage('Fluxo de Trabalho', 'Informações'), tabKey: 'informacoes' });
      }
      if (!groupId) {
        errors.push({ message: getRequiredFieldMessage('Grupo para Encaminhar', 'Informações'), tabKey: 'informacoes' });
      }
      if (workflowId && (!title || title.trim() === '')) {
        errors.push({ message: getRequiredFieldMessage('Título', 'Informações'), tabKey: 'informacoes' });
      }
      if (workflowId && (!description || description.trim() === '')) {
        errors.push({ message: getRequiredFieldMessage('Descrição', 'Informações'), tabKey: 'informacoes' });
      }
      if (!urgency) {
        errors.push({ message: getRequiredFieldMessage('Urgência', 'Informações'), tabKey: 'informacoes' });
      }
    }

    // Validação de campos customizados obrigatórios
    customFields.forEach((field) => {
      const isRequired = (field.step_is_required !== undefined && field.step_is_required !== null)
        ? !!field.step_is_required
        : !!field.is_required;
      const isBlocked = field.step_is_blocked !== undefined ? field.step_is_blocked : false;
      const isVisible = field.step_is_visible !== undefined ? field.step_is_visible : true;
      const tabName = getCustomFieldTabName(field);
      const tabKey = field.group_name || 'geral';
      const fieldLabel = field.label?.trim() || field.name?.trim() || `ID ${field.id}`;
      const isSupplierField = isSupplierCustomField(field);

      if (!isBlocked && isVisible && shouldShowField(field) && isSupplierField) {
        const supplierRaw = customFieldValues[field.id];
        const supplierError = validateSupplierSelectionValue(
          typeof supplierRaw === 'string' ? supplierRaw : '',
          workflows.find((w: Workflow) => String(w.id) === workflowId)?.name,
          field.options
        );
        if (supplierError) {
          errors.push({ message: `O campo "${fieldLabel}" ${supplierError}`, tabKey });
        }
      }

      // platform_document: validar também quando o utilizador escolhe um modo explícito,
      // mesmo em campo opcional, para evitar envio sem documento vinculado.
      if (field.field_type === 'platform_document' && areaType === 'purchases') {
        if (isBlocked || !isVisible || !shouldShowField(field)) return;
        const mode = platformDocumentMode[field.id] || 'upload';
        const hasFile =
          !!customFieldFiles[field.id] || hasSavedCustomFieldFile(customFieldSavedFiles[field.id]);
        const hasSelectedDoc = !!platformDocumentSelectedIds[field.id];
        const hasSelectedMode = mode !== 'upload' || hasFile || hasSelectedDoc;

        if (isRequired || hasSelectedMode) {
          if (mode === 'upload' && !hasFile) {
            errors.push({ message: getRequiredFieldMessage(fieldLabel, tabName), tabKey });
          } else if (mode === 'platform' && !hasSelectedDoc) {
            errors.push({ message: getRequiredFieldMessage(fieldLabel, tabName), tabKey });
          } else if (mode === 'create') {
            errors.push({
              message: `Após criar o documento, selecione-o em "Selecionar Documento da Plataforma" para o campo "${fieldLabel}" na aba "${tabName}"`,
              tabKey,
            });
          }
        }
        return;
      }

      if (!isRequired || isBlocked || !isVisible) return;
      // Item 38: obrigatoriedade só vale se a condição de exibição for atendida
      if (!shouldShowField(field)) return;

      if (field.field_type === 'platform_document') {
        if (areaType === 'purchases') {
          const mode = platformDocumentMode[field.id] || 'upload';
          if (mode === 'upload') {
            const fieldFile = customFieldFiles[field.id];
            const hasSaved = hasSavedCustomFieldFile(customFieldSavedFiles[field.id]);
            if (!fieldFile && !hasSaved) {
              errors.push({ message: getRequiredFieldMessage(fieldLabel, tabName), tabKey });
            }
          } else if (mode === 'platform') {
            const selectedDocId = platformDocumentSelectedIds[field.id];
            if (!selectedDocId) {
              errors.push({ message: getRequiredFieldMessage(fieldLabel, tabName), tabKey });
            }
          }
        }
        return;
      }

      if (field.field_type === 'file' || field.field_type === 'files') {
        const fieldFile = customFieldFiles[field.id];
        const hasSaved =
          areaType === 'purchases' && hasSavedCustomFieldFile(customFieldSavedFiles[field.id]);
        if (!fieldFile && !hasSaved) {
          errors.push({ message: getRequiredFieldMessage(fieldLabel, tabName), tabKey });
        }
        return;
      }

      const fieldValue = customFieldValues[field.id];
      const isEmpty = fieldValue === undefined ||
        fieldValue === null ||
        fieldValue === '' ||
        (Array.isArray(fieldValue) && fieldValue.length === 0) ||
        (field.field_type === 'table' && !hasTableContent(fieldValue)) ||
        (isSupplierField && getSupplierSelectionCount(typeof fieldValue === 'string' ? fieldValue : '') === 0);

      if (isEmpty) {
        errors.push({ message: getRequiredFieldMessage(fieldLabel, tabName), tabKey });
      }
    });

    if (errors.length > 0) {
      const tabToSelect = errors[0].tabKey;
      const detailsText = errors.map((err) => `• ${err.message}`).join('\n');
      const showValidationAlert = () => {
        showAlert({
          title: 'Campos Obrigatórios Não Preenchidos',
          message: 'Revise os campos pendentes abaixo:',
          variant: 'warning',
          details: detailsText,
        });
      };
      if (areaType === 'tickets') {
        setActiveTab(tabToSelect);
        // Dois frames: primeiro pinta a troca de aba, depois exibe o alerta
        requestAnimationFrame(() => {
          requestAnimationFrame(showValidationAlert);
        });
      } else {
        showValidationAlert();
      }
      return;
    }
    
    // Validação condicional de campos customizados (regras de validação)
    const validationErrors: string[] = [];
    customFields.forEach((field) => {
      if (field.validation_field_id && field.validation_rules) {
        const validationFieldValue = customFieldValues[Number(field.validation_field_id)];
        const currentFieldValue = customFieldValues[field.id];
        
        if (
          !isValidationSourceValueEmpty(validationFieldValue) &&
          currentFieldValue !== undefined &&
          currentFieldValue !== null &&
          currentFieldValue !== ''
        ) {
          try {
            const rules = parseValidationRulesJson(field.validation_rules);
            if (rules?.rules && Array.isArray(rules.rules)) {
              // Validar cada regra (todas devem ser satisfeitas - AND)
              rules.rules.forEach((rule: any) => {
                const effectiveRuleFieldId =
                  rule.field_id !== undefined && rule.field_id !== null && rule.field_id !== ''
                    ? Number(rule.field_id)
                    : field.validation_field_id;
                const ruleField = findWorkflowFieldById(customFields, effectiveRuleFieldId);
                if (!ruleField) return;
                
                let isValid = true;
                let errorMessage = '';
                
                // Aplicar operador de comparação
                switch (rule.operator) {
                  case 'equals':
                    isValid = String(validationFieldValue).toLowerCase().trim() === String(rule.value).toLowerCase().trim();
                    if (!isValid) {
                      errorMessage = `O campo "${ruleField.label}" deve ser igual a "${rule.value}"`;
                    }
                    break;
                  
                  case 'not_equals':
                    isValid = String(validationFieldValue).toLowerCase().trim() !== String(rule.value).toLowerCase().trim();
                    if (!isValid) {
                      errorMessage = `O campo "${ruleField.label}" não deve ser igual a "${rule.value}"`;
                    }
                    break;
                  
                  case 'contains':
                    isValid = String(validationFieldValue).toLowerCase().includes(String(rule.value).toLowerCase());
                    if (!isValid) {
                      errorMessage = `O campo "${ruleField.label}" deve conter "${rule.value}"`;
                    }
                    break;
                  
                  case 'greater_than':
                    const val1 = parseFieldNumericValue(validationFieldValue, ruleField.field_type);
                    const ruleVal1 = parseFloat(String(rule.value));
                    isValid = !isNaN(val1) && !isNaN(ruleVal1) && val1 > ruleVal1;
                    if (!isValid) {
                      errorMessage = `O campo "${ruleField.label}" deve ser maior que ${rule.value}`;
                    }
                    break;
                  
                  case 'greater_equal':
                    const val2 = parseFieldNumericValue(validationFieldValue, ruleField.field_type);
                    const ruleVal2 = parseFloat(String(rule.value));
                    isValid = !isNaN(val2) && !isNaN(ruleVal2) && val2 >= ruleVal2;
                    if (!isValid) {
                      errorMessage = `O campo "${ruleField.label}" deve ser maior ou igual a ${rule.value}`;
                    }
                    break;
                  
                  case 'less_than':
                    const val3 = parseFieldNumericValue(validationFieldValue, ruleField.field_type);
                    const ruleVal3 = parseFloat(String(rule.value));
                    isValid = !isNaN(val3) && !isNaN(ruleVal3) && val3 < ruleVal3;
                    if (!isValid) {
                      errorMessage = `O campo "${ruleField.label}" deve ser menor que ${rule.value}`;
                    }
                    break;
                  
                  case 'less_equal':
                    const val4 = parseFieldNumericValue(validationFieldValue, ruleField.field_type);
                    const ruleVal4 = parseFloat(String(rule.value));
                    isValid = !isNaN(val4) && !isNaN(ruleVal4) && val4 <= ruleVal4;
                    if (!isValid) {
                      errorMessage = `O campo "${ruleField.label}" deve ser menor ou igual a ${rule.value}`;
                    }
                    break;
                  
                  case 'min_date_days':
                    // Prazo mínimo é apenas orientativo: o aviso já aparece no campo e o envio segue liberado.
                    break;
                  
                  case 'value_ranges':
                    // Validação por faixas de valor
                    if (rule.value_ranges && Array.isArray(rule.value_ranges)) {
                      const validationValue = parseFieldNumericValue(validationFieldValue, ruleField.field_type);
                      if (!isNaN(validationValue)) {
                        const picked = pickValueRangeValidationForNumericValue(
                          rule.value_ranges,
                          validationValue,
                          evaluateConditionSatisfied,
                          customFieldValues
                        );
                        const applicableRange = picked ? { validation: picked.validation } : null;

                        // Aplicar validação baseada na faixa encontrada (mais restritiva se houver sobreposição)
                        if (applicableRange && applicableRange.validation) {
                          const validation = applicableRange.validation;
                          isValid = true;
                          
                          switch (validation.type) {
                            case 'min_date_days':
                              // Prazo mínimo é apenas orientativo: o aviso já aparece no campo e o envio segue liberado.
                              break;

                            case 'min_value':
                              if (typeof validation.value === 'number') {
                                const currentValue = parseFloat(String(currentFieldValue));
                                isValid = !isNaN(currentValue) && currentValue >= validation.value;
                                if (!isValid) {
                                  errorMessage = validation.message || 
                                    `O campo "${field.label}" deve ser maior ou igual a ${validation.value}`;
                                }
                              }
                              break;
                              
                            case 'max_value':
                              if (typeof validation.value === 'number') {
                                const currentValue = parseFloat(String(currentFieldValue));
                                isValid = !isNaN(currentValue) && currentValue <= validation.value;
                                if (!isValid) {
                                  errorMessage = validation.message || 
                                    `O campo "${field.label}" deve ser menor ou igual a ${validation.value}`;
                                }
                              }
                              break;
                              
                            case 'equals':
                              isValid = String(currentFieldValue).toLowerCase().trim() === String(validation.value).toLowerCase().trim();
                              if (!isValid) {
                                errorMessage = validation.message || 
                                  `O campo "${field.label}" deve ser igual a "${validation.value}"`;
                              }
                              break;
                          }
                        } else {
                          isValid = false;
                          errorMessage = `Nenhuma faixa de valor aplicável encontrada para o valor ${validationValue}`;
                        }
                      }
                    }
                    break;
                  
                  default:
                    // Formato antigo: compatibilidade com regras antigas
                    if (rule.value_range) {
                      const validationValue = parseFieldNumericValue(validationFieldValue, ruleField.field_type);
                      if (!isNaN(validationValue)) {
                        const min = rule.value_range.min ?? -Infinity;
                        const max = rule.value_range.max ?? Infinity;
                        isValid = validationValue >= min && (max === null || validationValue <= max);
                        // rule.min_date_days não é checado aqui: prazo mínimo é apenas orientativo.
                      }
                    }
                }
                
                if (!isValid && errorMessage) {
                  validationErrors.push(errorMessage);
                }
              });
            }
          } catch (error) {
            console.error('Erro ao validar campo condicional:', error);
          }
        }
      }
    });
    
    if (validationErrors.length > 0) {
      const firstTabKey = 'informacoes'; // regras condicionais podem estar em qualquer aba; foco em informacoes como padrão
      if (areaType === 'tickets') {
        setActiveTab(firstTabKey);
      }
      showAlert({
        title: 'Revise os campos',
        message: 'Os seguintes campos não atendem aos critérios de validação:',
        variant: 'warning',
        details: validationErrors.map(error => `• ${error}`).join('\n')
      });
      return;
    }
    
    setLoading(true);
    if (areaType === 'purchases') {
      cancelPendingDraftAutosave();
    }

    try {
      let ticketId: number;
      const apiEndpoint = areaType === 'purchases' ? '/api/purchases' : '/api/tickets';
      
      // Se for um rascunho de purchase, finalizar o rascunho
      if (areaType === 'purchases' && isDraft && draftId) {
        // Atualizar o rascunho com os dados finais antes de finalizar
        const draftFinalizePayload: Record<string, unknown> = {
          title: object,
          object,
          items_description: itemsDescription,
          description,
          urgency,
          workflow_id: parseInt(workflowId),
          assigned_group_id: parseInt(groupId),
          requester_id: currentUser?.id ?? null,
          status: PURCHASE_DRAFT_STATUS,
        };
        if (canManagePriority) {
          draftFinalizePayload.priority = priority;
        }
        try {
          await axios.put(purchaseDraftItemUrl(API_URL, draftId), draftFinalizePayload);
        } catch (draftUpdateError) {
          throwNotADraftForSubmit(draftUpdateError);
        }

        // Persistir campos customizados (ex.: Gerente do Contrato) ANTES do finalize,
        // para process_custom_field_approvers enxergar os valores no envio.
        const draftCustomFieldPromises = customFields
          .filter(field => {
            const fieldValue = customFieldValues[field.id];
            const mode = platformDocumentMode[field.id] || 'upload';
            if (field.field_type === 'platform_document') {
              return mode === 'platform' && fieldValue !== undefined && fieldValue !== '' && fieldValue !== null;
            }
            if (field.field_type === 'check' && !field.allow_multiple) {
              return hasCheckFieldPersistedValue(field, fieldValue);
            }
            return fieldValue !== undefined && fieldValue !== '' && fieldValue !== null &&
                   !(Array.isArray(fieldValue) && fieldValue.length === 0) &&
                   !(field.field_type === 'table' && !hasTableContent(fieldValue)) &&
                   field.field_type !== 'file' && field.field_type !== 'files';
          })
          .map(async (field) => {
            const fieldValue = customFieldValues[field.id];
            const valueData: any = {
              purchase_request_id: draftId,
              custom_field_id: field.id,
            };

            if (field.field_type === 'platform_document' && platformDocumentMode[field.id] === 'platform') {
              const docId = platformDocumentSelectedIds[field.id];
              if (docId) {
                valueData.document_instance_id = docId;
                valueData.text_value = null;
                valueData.file_path = null;
                valueData.file_paths = null;
              }
            } else if (field.field_type === 'table') {
              valueData.text_value = serializeTableValue(fieldValue);
            } else if (field.field_type === 'currency' || field.field_type === 'period') {
              valueData.text_value = typeof fieldValue === 'string' ? fieldValue : JSON.stringify(fieldValue);
            } else if (field.field_type === 'list' || field.field_type === 'combo' || (field.field_type === 'check' && field.allow_multiple)) {
              valueData.text_value = field.allow_multiple && Array.isArray(fieldValue)
                ? JSON.stringify(fieldValue)
                : String(fieldValue);
            } else if (field.field_type === 'check' && !field.allow_multiple) {
              const checkSerialized = serializeCheckFieldValue(field, fieldValue);
              if (!checkSerialized) return;
              valueData.text_value = checkSerialized;
            } else {
              valueData.text_value = String(fieldValue);
            }

            await upsertPurchaseCustomFieldValue(API_URL, valueData);
          });
        await Promise.all(draftCustomFieldPromises);

        const finalizeResponse = await axios.post(purchaseDraftFinalizeUrl(API_URL, draftId));
        ticketId = finalizeResponse.data.id;

        // Limpar rascunho do localStorage
        if (currentUser) {
          localStorage.removeItem(`purchase_draft_${currentUser.id}`);
        }
      } else {
        // Criar novo ticket ou requisição
        const ticketData: any = {
          title: areaType === 'purchases' ? object : title, // Para purchases, usar object como title
          description: areaType === 'purchases' ? itemsDescription : description,
          workflow_id: parseInt(workflowId),
          assigned_group_id: parseInt(groupId),
        };

        if (areaType === 'tickets') {
          ticketData.urgency = urgency;
        }
        
        if (areaType === 'purchases') {
          ticketData.requester_id = currentUser?.id ?? null;
        } else if (areaType === 'tickets' && requesterId) {
          ticketData.requester_id = requesterId;
        }
        if (areaType === 'purchases') {
          ticketData.urgency = urgency;
          if (canManagePriority) {
            ticketData.priority = priority;
          }
          ticketData.object = object;
          ticketData.items_description = itemsDescription;
        }
        
        const ticketResponse = await axios.post(`${API_URL}${apiEndpoint}/`, ticketData);
        ticketId = ticketResponse.data.id;
        
        // Limpar rascunho do localStorage se houver
        if (currentUser && areaType === 'purchases') {
          localStorage.removeItem(`purchase_draft_${currentUser.id}`);
        }
      }
      
      // Falhas de campo não impedem a criação, mas precisam ser reportadas:
      // salvar em silêncio faria o usuário acreditar que o dado foi persistido.
      const persistenceErrors: string[] = [];
      const describeFieldError = (
        field: { id: number; label?: string | null; name?: string | null },
        error: unknown
      ): string => {
        const label = field.label?.trim() || field.name?.trim() || `ID ${field.id}`;
        const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data
          ?.detail;
        return `${label}: ${typeof detail === 'string' ? detail : 'não foi possível salvar'}`;
      };

      // Salvar valores dos campos customizados em paralelo (muito mais rápido)
      const eligibleCustomFields = customFields
        .filter(field => {
          const fieldValue = customFieldValues[field.id];
          const mode = platformDocumentMode[field.id] || 'upload';
          // Incluir platform_document apenas se estiver no modo 'platform' com documento selecionado
          if (field.field_type === 'platform_document') {
            return mode === 'platform' && fieldValue !== undefined && fieldValue !== '' && fieldValue !== null;
          }
          if (field.field_type === 'check' && !field.allow_multiple) {
            return hasCheckFieldPersistedValue(field, fieldValue);
          }
          return fieldValue !== undefined && fieldValue !== '' && fieldValue !== null && 
                 !(Array.isArray(fieldValue) && fieldValue.length === 0) &&
                 !(field.field_type === 'table' && !hasTableContent(fieldValue)) &&
                 field.field_type !== 'file' && field.field_type !== 'files'; // Arquivos são tratados separadamente
        });

      const saveCustomFieldValue = (field: (typeof eligibleCustomFields)[number]) => {
          const fieldValue = customFieldValues[field.id];
          const valueData: any = areaType === 'purchases' 
            ? { purchase_request_id: ticketId, custom_field_id: field.id }
            : { ticket_id: ticketId, custom_field_id: field.id };
          
          // Tratar campo platform_document no modo 'platform'
          if (field.field_type === 'platform_document' && platformDocumentMode[field.id] === 'platform') {
            const docId = platformDocumentSelectedIds[field.id];
            if (docId) {
              valueData.document_instance_id = docId;
              valueData.text_value = null;
              valueData.file_path = null;
              valueData.file_paths = null;
            }
          } else if (field.field_type === 'table') {
            valueData.text_value = serializeTableValue(fieldValue);
          } else if (field.field_type === 'currency' || field.field_type === 'period') {
            // Campos que são salvos como JSON
            valueData.text_value = typeof fieldValue === 'string' ? fieldValue : JSON.stringify(fieldValue);
          } else if (field.field_type === 'list' || field.field_type === 'combo' || (field.field_type === 'check' && field.allow_multiple)) {
            // Campos de lista/combo/check com múltiplas opções
            valueData.text_value = field.allow_multiple && Array.isArray(fieldValue)
              ? JSON.stringify(fieldValue)
              : String(fieldValue);
          } else if (field.field_type === 'check' && !field.allow_multiple) {
            const checkSerialized = serializeCheckFieldValue(field, fieldValue);
            if (!checkSerialized) {
              return Promise.resolve(null);
            }
            valueData.text_value = checkSerialized;
          } else {
            // Campos de texto, número, data, link, user, analyst, etc.
            valueData.text_value = String(fieldValue);
          }
          
          return upsertCustomFieldValue(
            API_URL,
            areaType === 'purchases' ? 'purchases' : 'tickets',
            ticketId,
            valueData
          ).catch((error: unknown) => {
            console.error(`Erro ao salvar campo customizado ${field.id}:`, error);
            // Não bloquear a criação do ticket se houver erro ao salvar campos customizados
            persistenceErrors.push(describeFieldError(field, error));
            return null;
          });
      };

      // Campos de aprovação por último (inclusive depois dos anexos): gravá-los antes
      // coloca a requisição em "aguardando aprovação" e o backend passa a recusar
      // tudo que ainda não foi persistido.
      const { regularFields, approvalFields } = partitionFieldsForSave(eligibleCustomFields);
      await Promise.all(regularFields.map(saveCustomFieldValue));
      
      // Upload: compras usam helper (só arquivos novos); tickets mantêm fluxo atual
      if (areaType === 'purchases') {
        const uploadResult = await uploadPurchaseCustomFieldFiles({
          apiUrl: API_URL,
          purchaseRequestId: ticketId,
          customFields,
          customFieldFiles,
          platformDocumentMode,
        });
        if (uploadResult.errors.length > 0) {
          console.error('Falhas de upload em compras:', uploadResult.errors);
          persistenceErrors.push(...uploadResult.errors);
        }
      } else {
        const fileUploadPromises = customFields
          .filter(field => {
            const fieldFile = customFieldFiles[field.id];
            const mode = platformDocumentMode[field.id] || 'upload';
            if (field.field_type === 'platform_document') {
              return mode === 'upload' && fieldFile;
            }
            return (field.field_type === 'file' || field.field_type === 'files') && fieldFile;
          })
          .map(async (field) => {
            const fieldFile = customFieldFiles[field.id];
            const mode = platformDocumentMode[field.id] || 'upload';
            if (!fieldFile) return;

            try {
              if (field.field_type === 'file' || (field.field_type === 'platform_document' && mode === 'upload')) {
                const formData = new FormData();
                formData.append('file', fieldFile as File);
                await axios.post(
                  `${API_URL}${apiEndpoint}/${ticketId}/custom-fields/${field.id}/upload`,
                  formData,
                  { headers: { 'Content-Type': 'multipart/form-data' } }
                );
              } else if (field.field_type === 'files') {
                const formData = new FormData();
                if (Array.isArray(fieldFile)) {
                  fieldFile.forEach((f) => formData.append('files', f));
                } else {
                  formData.append('files', fieldFile as File);
                }
                await axios.post(
                  `${API_URL}${apiEndpoint}/${ticketId}/custom-fields/${field.id}/upload-multiple`,
                  formData,
                  { headers: { 'Content-Type': 'multipart/form-data' } }
                );
              }
            } catch (error: any) {
              console.error(`Erro ao fazer upload do arquivo para campo ${field.id}:`, error);
              persistenceErrors.push(describeFieldError(field, error));
            }
          });
        await Promise.all(fileUploadPromises);
      }

      // Compras gravam todos os aprovadores de uma vez: a API processa as aprovações
      // uma única vez no fim, então um segundo campo aprovador não é recusado.
      let approvalFieldsSaved = false;
      if (areaType === 'purchases' && approvalFields.length > 0) {
        const approvalItems = approvalFields
          .map((field) => ({
            custom_field_id: field.id,
            text_value: String(customFieldValues[field.id]),
          }))
          .filter((item) => item.text_value && item.text_value !== 'undefined');
        try {
          const batchResult = await batchUpsertPurchaseCustomFieldValues(
            API_URL,
            ticketId,
            approvalItems
          );
          batchResult.errors.forEach((batchError) => {
            const field = approvalFields.find((item) => item.id === batchError.custom_field_id);
            const label = field?.label?.trim() || field?.name?.trim() || `ID ${batchError.custom_field_id}`;
            persistenceErrors.push(`${label}: ${batchError.detail}`);
          });
          approvalFieldsSaved = true;
        } catch (error: unknown) {
          if (!isBatchEndpointUnavailable(error)) {
            throw error;
          }
        }
      }
      if (!approvalFieldsSaved) {
        await Promise.all(approvalFields.map(saveCustomFieldValue));
      }
      
      setTitle('');
      setObject('');
      setItemsDescription('');
      setDescription('');
      setPriority('p3');
      setUrgency('');
      setWorkflowId('');
      setGroupId('');
      setRequesterId(null);
      setRequesterSearchTerm('');
      setDebouncedRequesterQuery('');
      setSelectedRequester(null);
      setFilteredRequesterUsers([]);
      setCustomFields([]);
      setCustomFieldValues({});
      setCustomFieldFiles({});
      setCustomFieldSavedFiles({});
      setTemplateCategory('');
      setPlatformDocumentMode({});
      setPlatformDocumentSelectedIds({});
      setDraftId(null);
      setIsDraft(false);
      setDraftSavedMessage(null);
      
      // Atualizar dados
      onSuccess();
      
      // Mostrar confirmação primeiro
      if (persistenceErrors.length > 0) {
        showAlert({
          title: `${areaType === 'purchases' ? 'Requisição' : 'Ticket'} criado com pendências`,
          message: `${persistenceErrors.length} campo(s) não foram salvos. Reabra o registro e preencha novamente.`,
          variant: 'warning',
          details: persistenceErrors.join('\n')
        });
      } else {
        showAlert({
          title: 'Sucesso',
          message: `${areaType === 'purchases' ? 'Requisição' : 'Ticket'} criado com sucesso!`,
          variant: 'success'
        });
      }
      
      // Fechar modal após um delay para garantir que o alerta seja exibido
      setTimeout(() => {
        onHide();
      }, 300);
    } catch (error: any) {
      if (areaType === 'purchases') {
        stopDraftAutosaveRef.current = false;
      }
      console.error(`Erro ao criar ${areaType === 'purchases' ? 'requisição' : 'ticket'}:`, error);
      showAlert({
        title: `Erro ao Criar ${areaType === 'purchases' ? 'Requisição' : 'Ticket'}`,
        message: `Não foi possível criar ${areaType === 'purchases' ? 'a requisição' : 'o ticket'}.`,
        variant: 'danger',
        details: (() => {
          if (error.response?.data?.detail) {
            const detail = error.response.data.detail;
            if (Array.isArray(detail)) {
              return detail.map((d: any) => `${d.loc?.join('.') || ''}: ${d.msg || ''}`).join('\n');
            } else if (typeof detail === 'object') {
              return JSON.stringify(detail);
            } else {
              return String(detail);
            }
          }
          return 'Erro desconhecido';
        })()
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="lg"
        centered
        dialogClassName="ticket-create-modal-dialog"
        className="dashboard-themed-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
          {areaType === 'purchases' ? 'Nova Requisição de Compra' : 'Novo Ticket'}
          {isDraft && (
            <Badge bg="warning" text="dark" className="ms-2">Rascunho</Badge>
          )}
          {isSavingDraft && (
            <span className="ms-2 text-muted" style={{ fontSize: '0.8rem' }}>
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              {' '}Salvando...
            </span>
          )}
          {draftSavedMessage && (
            <span className="ms-2 text-success" style={{ fontSize: '0.8rem' }}>
              {draftSavedMessage}
            </span>
          )}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit} noValidate>
          <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {areaType === 'purchases' ? (
            <React.Fragment>
              <Form.Group className="mb-3">
                <Form.Label>Objeto <span className="text-danger">*</span></Form.Label>
                <Form.Text className="text-muted d-block mb-2" style={{ whiteSpace: 'pre-line', fontSize: '0.875rem', fontStyle: 'italic' }}>
                  {PURCHASE_OBJECT_HELP_TEXT}
                </Form.Text>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={object}
                  onChange={(e) => setObject(e.target.value)}
                  required
                  autoFocus
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Descrição dos Itens <span className="text-danger">*</span></Form.Label>
                <Form.Text className="text-muted d-block mb-2" style={{ whiteSpace: 'pre-line', fontSize: '0.875rem', fontStyle: 'italic' }}>
                  {PURCHASE_ITEMS_HELP_TEXT}
                </Form.Text>
                <Form.Control
                  as="textarea"
                  rows={6}
                  value={itemsDescription}
                  onChange={(e) => setItemsDescription(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Tipo de Contratação <span className="text-danger">*</span></Form.Label>
                <Form.Text className="text-muted d-block mb-2" style={{ whiteSpace: 'pre-line', fontSize: '0.875rem', fontStyle: 'italic' }}>
                  {PURCHASE_WORKFLOW_HELP_TEXT}
                </Form.Text>
                <Form.Select
                  value={workflowId}
                  onChange={(e) => setWorkflowId(e.target.value)}
                  required
                >
                  <option value="">Selecione um tipo</option>
                  {workflows
                    .filter((w: Workflow) => w.is_active)
                    .sort((a: Workflow, b: Workflow) => a.name.localeCompare(b.name, 'pt-BR'))
                    .map((workflow: Workflow) => (
                      <option key={workflow.id} value={String(workflow.id)}>
                        {workflow.name}
                      </option>
                    ))}
                </Form.Select>
                {workflowId && (() => {
                  const selectedWorkflow = workflows.find((w: Workflow) => String(w.id) === workflowId);
                  return selectedWorkflow ? (
                    <div className="mt-2 p-3 workflow-info-box rounded">
                      <strong className="workflow-info-title">Tipo Selecionado:</strong> {selectedWorkflow.name}
                      {selectedWorkflow.description && (
                        <div className="mt-2">
                          <small className="workflow-info-description">{selectedWorkflow.description}</small>
                        </div>
                      )}
                    </div>
                  ) : null;
                })()}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Urgência <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  value={urgency}
                  onChange={(e) => setUrgency(e.target.value as TicketUrgency)}
                  required
                  disabled={!workflowId}
                  aria-label="Selecione a urgência da requisição"
                >
                  <option value="" disabled>Selecione a urgência</option>
                  {URGENCY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              {canManagePriority && (
                <Form.Group className="mb-3">
                  <Form.Label>Prioridade</Form.Label>
                  <Form.Select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    disabled={!workflowId}
                  >
                    <option value="p1">P1</option>
                    <option value="p2">P2</option>
                    <option value="p3">P3</option>
                    <option value="p4">P4</option>
                  </Form.Select>
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Solicitante</Form.Label>
                <Form.Control
                  type="text"
                  value={currentUser?.email || ''}
                  disabled
                  readOnly
                  aria-label="Solicitante (usuário logado)"
                />
                <Form.Text className="text-muted">
                  O solicitante é sempre o usuário logado e não pode ser alterado.
                </Form.Text>
              </Form.Group>
            </React.Fragment>
          ) : (
            <Tabs
              activeKey={activeTab}
              onSelect={(tabKey) => setActiveTab(tabKey || 'informacoes')}
              className="mb-3"
            >
              <Tab eventKey="informacoes" title="Informações">
                <div className="pt-3">
                  <Form.Group className="mb-3">
                    <Form.Label>Fluxo de Trabalho <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      value={workflowId}
                      onChange={(e) => setWorkflowId(e.target.value)}
                      required
                      autoFocus
                    >
                      <option value="">Selecione um fluxo</option>
                      {workflows.filter((w: Workflow) => w.is_active).map((workflow: Workflow) => (
                        <option key={workflow.id} value={String(workflow.id)}>
                          {workflow.name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Todo ticket deve estar associado a um fluxo de trabalho.
                    </Form.Text>
                    {workflowId && (() => {
                      const selectedWorkflow = workflows.find((w: Workflow) => String(w.id) === workflowId);
                      return selectedWorkflow ? (
                        <div className="mt-2 p-3 workflow-info-box rounded">
                          <strong className="workflow-info-title">Fluxo Selecionado:</strong> {selectedWorkflow.name}
                          {selectedWorkflow.description && (
                            <div className="mt-2">
                              <small className="workflow-info-description">{selectedWorkflow.description}</small>
                            </div>
                          )}
                        </div>
                      ) : null;
                    })()}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Título <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      required={!!workflowId}
                      disabled={!workflowId}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Descrição <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      required={!!workflowId}
                      disabled={!workflowId}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Urgência <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      value={urgency}
                      onChange={(e) => setUrgency(e.target.value as TicketUrgency)}
                      required
                      disabled={!workflowId}
                      aria-label="Selecione a urgência do chamado"
                    >
                      <option value="" disabled>Selecione a urgência</option>
                      {URGENCY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Solicitante</Form.Label>
                    <div style={{ position: 'relative' }}>
                      <Form.Control
                        type="text"
                        placeholder={currentUser ? `Padrão: ${currentUser.name} — digite para buscar outro` : 'Digite nome ou e-mail para buscar...'}
                        value={
                          requesterId && selectedRequester
                            ? `${selectedRequester.name} (${selectedRequester.email})`
                            : requesterSearchTerm
                        }
                        onChange={(e) => {
                          const v = e.target.value;
                          setRequesterSearchTerm(v);
                          setRequesterId(null);
                          setSelectedRequester(null);
                          if (!v.trim()) {
                            setFilteredRequesterUsers([]);
                          }
                        }}
                        disabled={!workflowId}
                      />
                      {requesterSearchTerm.trim() && !requesterId && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            zIndex: 1000,
                            backgroundColor: 'var(--bs-body-bg)',
                            border: '1px solid var(--bs-border-color)',
                            borderRadius: '4px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                        >
                          {(requesterSearchLoading ||
                            requesterSearchTerm.trim() !== debouncedRequesterQuery.trim()) && (
                            <div className="px-3 py-2 text-muted small">Buscando...</div>
                          )}
                          {!requesterSearchLoading &&
                            requesterSearchTerm.trim() === debouncedRequesterQuery.trim() &&
                            debouncedRequesterQuery.trim().length >= 1 &&
                            filteredRequesterUsers.length === 0 && (
                              <div className="px-3 py-2 text-muted small">Nenhum usuário encontrado</div>
                            )}
                          {!requesterSearchLoading &&
                            requesterSearchTerm.trim() === debouncedRequesterQuery.trim() &&
                            filteredRequesterUsers.map((user) => (
                              <div
                                key={user.id}
                                style={{
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid var(--bs-border-color)',
                                }}
                                onClick={() => {
                                  setRequesterId(user.id);
                                  setSelectedRequester(user);
                                  setRequesterSearchTerm('');
                                  setDebouncedRequesterQuery('');
                                  setFilteredRequesterUsers([]);
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = 'var(--bs-tertiary-bg)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'var(--bs-body-bg)';
                                }}
                              >
                                {user.name} ({user.email})
                              </div>
                            ))}
                        </div>
                      )}
                      {requesterId && (
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => {
                            setRequesterId(null);
                            setSelectedRequester(null);
                            setRequesterSearchTerm('');
                            setDebouncedRequesterQuery('');
                            setFilteredRequesterUsers([]);
                          }}
                          style={{ position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)', padding: '0 5px' }}
                        >
                          ✕
                        </Button>
                      )}
                    </div>
                    <Form.Text className="text-muted">
                      Selecione o usuário que está solicitando o ticket. Se não selecionar, você será o solicitante.
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-0">
                    <Form.Label>Grupo para Encaminhar <span className="text-danger">*</span></Form.Label>
                    <Form.Select 
                      value={groupId} 
                      onChange={(e) => setGroupId(e.target.value)}
                      required={!(!workflowId || availableGroups.length === 0)}
                      disabled={!workflowId || availableGroups.length === 0}
                    >
                      <option value="">
                        {!workflowId 
                          ? 'Selecione um fluxo primeiro' 
                          : availableGroups.length === 0 
                            ? 'Nenhum grupo disponível para este fluxo'
                            : 'Selecione um grupo'}
                      </option>
                      {availableGroups.map((group: Group) => (
                        <option key={group.id} value={String(group.id)}>
                          {group.name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      {workflowId
                        ? 'Selecione um grupo associado ao fluxo de trabalho escolhido.'
                        : 'Todo ticket deve ser encaminhado para um grupo associado ao fluxo.'}
                    </Form.Text>
                  </Form.Group>
                </div>
              </Tab>

              {customFields.length > 0 ? (() => {
                const groupedFields: Record<string, WorkflowCustomField[]> = {};
                const ungroupedFields: WorkflowCustomField[] = [];

                customFields.forEach(field => {
                  const isVisible = field.step_is_visible !== undefined ? field.step_is_visible : true;
                  if (!isVisible) return;

                  if (field.group_name) {
                    const groupName = field.group_name;
                    if (!groupedFields[groupName]) {
                      groupedFields[groupName] = [];
                    }
                    groupedFields[groupName].push(field);
                  } else {
                    ungroupedFields.push(field);
                  }
                });

                const hasGroups = Object.keys(groupedFields).length > 0;
                const hasUngrouped = ungroupedFields.length > 0;

                const customTabs = Object.entries(groupedFields).map(([groupName, fields]) => {
                  const visibleFields = fields.filter(shouldShowField);
                  return (
                    <Tab key={groupName} eventKey={groupName} title={groupName}>
                      <div className="pt-3">
                        {visibleFields.length > 0 ? (
                          visibleFields.map(renderField)
                        ) : (
                          <div className="text-muted mb-3">
                            <small>Preencha os campos dependentes na aba Informações para liberar os campos desta aba.</small>
                          </div>
                        )}
                      </div>
                    </Tab>
                  );
                });

                if (hasUngrouped) {
                  customTabs.push(
                    <Tab key="geral" eventKey="geral" title={hasGroups ? 'Geral' : 'Campos Customizados'}>
                      <div className="pt-3">
                        {(() => {
                          const visibleUngroupedFields = ungroupedFields.filter(shouldShowField);
                          if (visibleUngroupedFields.length > 0) {
                            return visibleUngroupedFields.map(renderField);
                          }
                          return (
                            <div className="text-muted mb-3">
                              <small>Preencha os campos dependentes na aba Informações para liberar os campos desta aba.</small>
                            </div>
                          );
                        })()}
                      </div>
                    </Tab>
                  );
                }

                return customTabs;
              })() : null}
            </Tabs>
          )}

          {/* Campos Customizados */}
          {areaType === 'purchases' && customFields.length > 0 ? (() => {
            // Agrupar campos por group_name
            const groupedFields: Record<string, WorkflowCustomField[]> = {};
            const ungroupedFields: WorkflowCustomField[] = [];

            customFields.forEach(field => {
              // Respeitar a configuração da primeira etapa
              const isVisible = field.step_is_visible !== undefined ? field.step_is_visible : true;
              if (!isVisible) return; // Não renderizar campos invisíveis
              
              // Verificar condições condicionais
              if (!shouldShowField(field)) return; // Não renderizar campos que não atendem condições
              
              if (field.group_name) {
                const groupName = field.group_name;
                if (!groupedFields[groupName]) {
                  groupedFields[groupName] = [];
                }
                groupedFields[groupName].push(field);
              } else {
                ungroupedFields.push(field);
              }
            });

            const hasGroups = Object.keys(groupedFields).length > 0;
            const hasUngrouped = ungroupedFields.length > 0;

            // Se não há grupos e há apenas campos sem agrupamento, exibir sem tabs
            if (!hasGroups && hasUngrouped) {
              return (
                <div className="mb-3">
                  <h6>Informações do Processo</h6>
                  {ungroupedFields.map(renderField)}
                </div>
              );
            }

            // Se há grupos, sempre exibir com tabs (mesmo que seja apenas um grupo)
            // Isso garante consistência visual e permite adicionar mais grupos no futuro
            const firstGroupKey = purchaseTabKeys.length > 0 ? purchaseTabKeys[0] : (Object.keys(groupedFields)[0] || 'ungrouped');
            const currentActiveTab = purchaseActiveTab || firstGroupKey;
            
            // Obter campos da aba ativa
            const getActiveTabFields = () => {
              if (currentActiveTab === 'ungrouped') {
                return ungroupedFields;
              }
              return groupedFields[currentActiveTab] || [];
            };
            
            const activeFields = getActiveTabFields();
            
            return (
              <div className="mb-3" ref={purchaseCustomFieldsRef}>
                <h6>Informações do Processo</h6>
                {/* Renderizar campos da aba ativa */}
                <div className="mb-2">
                  {activeFields.map(renderField)}
                </div>
                {/* Mostrar nomes das abas abaixo dos campos */}
                <div className="d-flex gap-2 flex-wrap mt-3 pt-2 border-top">
                  {purchaseTabKeys.map((tabKey) => {
                    const tabName = tabKey === 'ungrouped' ? 'Geral' : tabKey;
                    const isActive = currentActiveTab === tabKey;
                    return (
                      <span
                        key={tabKey}
                        className={`badge ${isActive ? 'bg-primary' : 'bg-secondary'}`}
                        style={{ 
                          fontSize: '0.9rem', 
                          padding: '6px 12px',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          setPurchaseActiveTab(tabKey);
                          // Focar no início dos campos após um pequeno delay
                          setTimeout(() => {
                            if (purchaseCustomFieldsRef.current) {
                              const firstField = purchaseCustomFieldsRef.current.querySelector('input, select, textarea') as HTMLElement;
                              if (firstField) {
                                firstField.focus();
                              }
                            }
                          }, 100);
                        }}
                      >
                        {tabName}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })() : null}

          {areaType === 'purchases' && (
            <Form.Group className="mb-3">
              <Form.Label>Grupo para Encaminhar <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                required={!(!workflowId || availableGroups.length === 0)}
                disabled={!workflowId || availableGroups.length === 0}
              >
                <option value="">
                  {!workflowId
                    ? 'Selecione um tipo primeiro'
                    : availableGroups.length === 0
                      ? 'Nenhum grupo disponível para este tipo'
                      : 'Selecione um grupo'}
                </option>
                {availableGroups.map((group: Group) => (
                  <option key={group.id} value={String(group.id)}>
                    {group.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                {workflowId
                  ? 'Selecione um grupo associado ao tipo de contratação escolhido.'
                  : 'Toda requisição deve ser encaminhada para um grupo associado ao tipo de contratação.'}
              </Form.Text>
            </Form.Group>
          )}

          </Modal.Body>
          <Modal.Footer className="d-flex flex-wrap gap-2">
          {/* Botões de navegação entre abas - apenas para purchases */}
          {areaType === 'purchases' && customFields.length > 0 && purchaseTabKeys.length > 1 && (() => {
            const currentIndex = purchaseTabKeys.indexOf(purchaseActiveTab);
            const canGoPrevious = currentIndex > 0;
            const canGoNext = currentIndex < purchaseTabKeys.length - 1;
            
            const navigateTab = (direction: 'prev' | 'next') => {
              if (direction === 'prev' && canGoPrevious) {
                const newTab = purchaseTabKeys[currentIndex - 1];
                setPurchaseActiveTab(newTab);
                setTimeout(() => {
                  if (purchaseCustomFieldsRef.current) {
                    const firstField = purchaseCustomFieldsRef.current.querySelector('input, select, textarea') as HTMLElement;
                    if (firstField) {
                      firstField.focus();
                    }
                  }
                }, 100);
              } else if (direction === 'next' && canGoNext) {
                const newTab = purchaseTabKeys[currentIndex + 1];
                setPurchaseActiveTab(newTab);
                setTimeout(() => {
                  if (purchaseCustomFieldsRef.current) {
                    const firstField = purchaseCustomFieldsRef.current.querySelector('input, select, textarea') as HTMLElement;
                    if (firstField) {
                      firstField.focus();
                    }
                  }
                }, 100);
              }
            };
            
            return (
              <div className="d-flex align-items-center me-auto">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => navigateTab('prev')}
                  disabled={!canGoPrevious}
                  style={{ 
                    minWidth: '36px',
                    padding: '6px 10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.2rem',
                    lineHeight: '1',
                    fontWeight: 'bold'
                  }}
                  title="Aba anterior"
                >
                  ◀
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => navigateTab('next')}
                  disabled={!canGoNext}
                  style={{ 
                    minWidth: '36px',
                    padding: '6px 10px',
                    marginLeft: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.2rem',
                    lineHeight: '1',
                    fontWeight: 'bold'
                  }}
                  title="Próxima aba"
                >
                  ▶
                </Button>
              </div>
            );
          })()}
          {areaType === 'purchases' && (
            <Button 
              variant="outline-primary" 
              onClick={async () => {
                // Salvar como rascunho sem validar campos obrigatórios
                try {
                  setLoading(true);
                  
                  const saved = await savePurchaseDraftRecord(
                    API_URL,
                    draftId,
                    buildPurchaseDraftPayload({
                      title,
                      object,
                      itemsDescription,
                      description,
                      urgency,
                      priority,
                      includePriority: canManagePriority,
                      workflowId,
                      groupId,
                      requesterId: currentUser?.id ?? null,
                    }),
                    { recreateIfNotDraft: true }
                  );
                  if (!saved.id) {
                    throw new Error('Não foi possível salvar o rascunho.');
                  }
                  const savedDraftId = saved.id;
                  setDraftId(savedDraftId);
                  setIsDraft(true);
                  
                  // Salvar campos personalizados
                  if (savedDraftId && customFields.length > 0) {
                    const customFieldPromises = customFields
                      .filter(field => {
                        const fieldValue = customFieldValues[field.id];
                        const mode = platformDocumentMode[field.id] || 'upload';
                        // Incluir platform_document no modo 'platform' com documento selecionado OU no modo 'upload' com arquivo selecionado
                        if (field.field_type === 'platform_document') {
                          if (mode === 'platform') {
                            // Verificar se há um documento selecionado em platformDocumentSelectedIds
                            const docId = platformDocumentSelectedIds[field.id];
                            return docId !== undefined && docId !== null;
                          } else if (mode === 'upload') {
                            // Incluir no modo upload se houver arquivo selecionado (será atualizado depois pelo upload)
                            return customFieldFiles[field.id] !== undefined;
                          }
                          return false;
                        }
                        // Campos de arquivo não são salvos aqui (são salvos separadamente via upload)
                        if (field.field_type === 'file' || field.field_type === 'files') {
                          return false;
                        }
                        if (field.field_type === 'check' && !field.allow_multiple) {
                          return hasCheckFieldPersistedValue(field, fieldValue);
                        }
                        return fieldValue !== undefined && fieldValue !== null && fieldValue !== '' && 
                               !(Array.isArray(fieldValue) && fieldValue.length === 0) &&
                               !(field.field_type === 'table' && !hasTableContent(fieldValue));
                      })
                      .map(async (field) => {
                        const fieldValue = customFieldValues[field.id];
                        const mode = platformDocumentMode[field.id] || 'upload';
                        const valueData: any = {
                          purchase_request_id: savedDraftId,
                          custom_field_id: field.id,
                        };
                        
                        // Tratar campo platform_document no modo 'platform'
                        if (field.field_type === 'platform_document' && mode === 'platform') {
                          const docId = platformDocumentSelectedIds[field.id];
                          if (docId) {
                            valueData.document_instance_id = docId;
                            valueData.text_value = null;
                            valueData.file_path = null;
                            valueData.file_paths = null;
                          }
                        } else if (field.field_type === 'platform_document' && mode === 'upload') {
                          // No modo upload, criar registro vazio que será atualizado depois pelo upload
                          valueData.document_instance_id = null;
                          valueData.text_value = null;
                          valueData.file_path = null;
                          valueData.file_paths = null;
                        } else if (field.field_type === 'table') {
                          valueData.text_value = serializeTableValue(fieldValue);
                        } else if (field.field_type === 'currency' || field.field_type === 'period') {
                          // Campos que são salvos como JSON
                          valueData.text_value = typeof fieldValue === 'string' ? fieldValue : JSON.stringify(fieldValue);
                        } else if (field.field_type === 'list' || field.field_type === 'combo' || (field.field_type === 'check' && field.allow_multiple)) {
                          // Campos de lista/combo/check com múltiplas opções
                          valueData.text_value = field.allow_multiple && Array.isArray(fieldValue)
                            ? JSON.stringify(fieldValue)
                            : String(fieldValue);
                        } else if (field.field_type === 'check' && !field.allow_multiple) {
                          const checkSerialized = serializeCheckFieldValue(field, fieldValue);
                          if (!checkSerialized) {
                            return;
                          }
                          valueData.text_value = checkSerialized;
                        } else {
                          // Campos de texto, número, data, link, user, analyst, etc.
                          valueData.text_value = String(fieldValue);
                        }
                        
                        try {
                          await upsertPurchaseCustomFieldValue(API_URL, valueData);
                        } catch (error) {
                          console.error(`Erro ao salvar campo customizado ${field.id}:`, error);
                        }
                      });
                    
                    await Promise.all(customFieldPromises);
                  }

                  if (savedDraftId) {
                    const draftUploadResult = await uploadPurchaseCustomFieldFiles({
                      apiUrl: API_URL,
                      purchaseRequestId: savedDraftId,
                      customFields,
                      customFieldFiles,
                      platformDocumentMode,
                    });

                    if (Object.keys(draftUploadResult.savedByFieldId).length > 0) {
                      setCustomFieldSavedFiles((prev) => ({
                        ...prev,
                        ...draftUploadResult.savedByFieldId,
                      }));
                      setCustomFieldFiles((prev) => {
                        const next = { ...prev };
                        draftUploadResult.uploadedFieldIds.forEach((fieldId) => {
                          next[fieldId] = null;
                        });
                        return next;
                      });
                    }

                    if (draftUploadResult.errors.length > 0) {
                      showAlert({
                        title: 'Rascunho salvo com avisos',
                        message:
                          'Os dados do rascunho foram salvos, mas alguns anexos falharam no upload.',
                        variant: 'warning',
                        details: draftUploadResult.errors.join('\n'),
                      });
                    } else {
                      showAlert({
                        title: 'Sucesso',
                        message: 'Rascunho salvo com sucesso!',
                        variant: 'success',
                      });
                    }
                  } else {
                    showAlert({
                      title: 'Sucesso',
                      message: 'Rascunho salvo com sucesso!',
                      variant: 'success',
                    });
                  }
                } catch (error: any) {
                  console.error('Erro ao salvar rascunho:', error);
                  let errorDetails = 'Erro desconhecido';
                  if (error.response?.data?.detail) {
                    const detail = error.response.data.detail;
                    if (Array.isArray(detail)) {
                      errorDetails = formatFastApiValidationErrors(detail);
                    } else if (typeof detail === 'object') {
                      errorDetails = JSON.stringify(detail);
                    } else {
                      errorDetails = String(detail);
                    }
                  }
                  showAlert({
                    title: 'Erro ao Salvar Rascunho',
                    message: 'Não foi possível salvar o rascunho.',
                    variant: 'danger',
                    details: errorDetails
                  });
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading}
            >
              <i className="bi bi-save me-2"></i>
              Salvar Como Rascunho
            </Button>
          )}
          {areaType === 'tickets' && (
            <div className="me-auto d-flex flex-wrap align-items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline-primary"
                onClick={() => {
                  setTemplateSearchTerm('');
                  setTemplateFilterCategory('');
                  setShowTemplatesModal(true);
                }}
              >
                Meus templates
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline-success"
                onClick={() => setShowSaveTemplateModal(true)}
                disabled={loading || savingTemplate}
              >
                Salvar como template
              </Button>
            </div>
          )}
          <Button variant="secondary" onClick={onHide}>
            Cancelar
          </Button>
          <Button variant="success" type="submit" disabled={loading}>
            {loading ? (areaType === 'purchases' ? 'Enviando...' : 'Criando...') : areaType === 'purchases' ? 'Enviar Requisição' : 'Criar Ticket'}
          </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    {areaType === 'tickets' && (
      <>
        <Modal
          show={showTemplatesModal}
          onHide={() => setShowTemplatesModal(false)}
          size="lg"
          centered
          dialogClassName="ticket-create-modal-dialog"
        >
          <Modal.Header closeButton>
            <Modal.Title>Meus templates</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="d-flex gap-2 mb-3">
              <Form.Control
                placeholder="Buscar templates"
                value={templateSearchTerm}
                onChange={(e) => setTemplateSearchTerm(e.target.value)}
              />
              <Form.Select
                value={templateFilterCategory}
                onChange={(e) => setTemplateFilterCategory(e.target.value)}
                className="csp-wave1-max-width-220"
              >
                <option value="">Todas categorias</option>
                {templateCategories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </Form.Select>
            </div>

            {loadingTemplates ? (
              <div className="text-muted">Carregando templates...</div>
            ) : filteredTemplateOptions.length === 0 ? (
              <div className="text-muted">Nenhum template encontrado.</div>
            ) : (
              <div className="table-responsive">
                <Table hover size="sm" className="align-middle">
                  <thead>
                    <tr>
                      <th>Título</th>
                      <th>Categoria</th>
                      <th>Descrição</th>
                      <th className="text-end">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTemplateOptions.map((template) => (
                      <tr key={template.id}>
                        <td>{template.title}</td>
                        <td>{template.category || <span className="text-muted">-</span>}</td>
                        <td className="csp-wave1-pre-wrap">{template.description}</td>
                        <td className="text-end">
                          <div className="d-flex justify-content-end gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline-primary"
                              onClick={() => {
                                applyTemplate(template);
                                setShowTemplatesModal(false);
                              }}
                            >
                              Usar
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleDeleteTemplate(template.id)}
                              disabled={deletingTemplateId === template.id}
                            >
                              {deletingTemplateId === template.id ? 'Excluindo...' : 'Excluir'}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="secondary" onClick={() => setShowTemplatesModal(false)}>
              Fechar
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showSaveTemplateModal}
          onHide={() => setShowSaveTemplateModal(false)}
          centered
          dialogClassName="ticket-create-modal-dialog"
        >
          <Modal.Header closeButton>
            <Modal.Title>Salvar template</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-0">
              <Form.Label>Categoria (opcional)</Form.Label>
              <Form.Control
                placeholder="Ex.: Impressora, Acesso, Rede..."
                value={templateCategory}
                onChange={(e) => setTemplateCategory(e.target.value)}
                list="dashboard-ticket-template-categories"
              />
              <datalist id="dashboard-ticket-template-categories">
                {templateCategories.map((item) => (
                  <option key={item} value={item} />
                ))}
              </datalist>
              <Form.Text className="text-muted">
                O template salva título, descrição e categoria.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="secondary" onClick={() => setShowSaveTemplateModal(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="success"
              onClick={handleSaveTemplate}
              disabled={savingTemplate}
            >
              {savingTemplate ? 'Salvando...' : 'Salvar template'}
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    )}
    {areaType === 'purchases' && (
      <DraftListModal
        show={showDraftListModal}
        onHide={() => setShowDraftListModal(false)}
        onSelectDraft={async (draft) => {
          setDraftId(draft.id);
          setIsDraft(true);
          setCustomFieldSavedFiles({});
          setCustomFieldFiles({});
          // Usar object, ou title se object não existir (compatibilidade com rascunhos antigos)
          setObject(draft.object || draft.title || '');
          setItemsDescription(draft.items_description || '');
          setDescription(draft.description || '');
          setPriority(draft.priority || 'p4');
          setUrgency((draft.urgency as TicketUrgency) || '');
          const groupIdStr = draft.assigned_group_id ? String(draft.assigned_group_id) : '';
          preserveDraftHydrationRef.current = true;
          pendingDraftGroupIdRef.current = groupIdStr || null;
          setWorkflowId(draft.workflow_id ? String(draft.workflow_id) : '');
          setGroupId(groupIdStr);
          if (currentUser) {
            setRequesterId(currentUser.id);
            setSelectedRequester(currentUser);
            setRequesterSearchTerm('');
            setDebouncedRequesterQuery('');
            setFilteredRequesterUsers([]);
          }
          
          // Carregar campos personalizados
          try {
            const customFieldsResponse = await axios.get(`${API_URL}/api/purchases/${draft.id}/custom-fields`);
            if (customFieldsResponse.data) {
              const customValues: { [fieldId: number]: any } = {};
              const platformModes: { [fieldId: number]: 'upload' | 'platform' | 'create' } = {};
              const platformDocIds: { [fieldId: number]: number | null } = {};
              const savedFiles: { [fieldId: number]: SavedCustomFieldFile } = {};
              
              customFieldsResponse.data.forEach((cf: any) => {
                // Verificar se é campo platform_document com document_instance_id
                if (cf.document_instance_id) {
                  platformModes[cf.custom_field_id] = 'platform';
                  platformDocIds[cf.custom_field_id] = cf.document_instance_id;
                  customValues[cf.custom_field_id] = cf.document_instance_id.toString();
                } else if (cf.file_path || cf.file_paths) {
                  const parsedSaved = parseSavedCustomFieldFilesFromApi(cf);
                  if (parsedSaved) savedFiles[cf.custom_field_id] = parsedSaved;
                } else if (cf.text_value != null && String(cf.text_value) !== '') {
                  customValues[cf.custom_field_id] = hydrateCustomFieldTextValue(String(cf.text_value));
                }
              });
              setCustomFieldValues(customValues);
              setCustomFieldSavedFiles(savedFiles);
              setCustomFieldFiles({});
              setPlatformDocumentMode(prev => ({ ...prev, ...platformModes }));
              setPlatformDocumentSelectedIds(prev => ({ ...prev, ...platformDocIds }));
            }
          } catch (error) {
            console.error('Erro ao carregar campos personalizados:', error);
          }
        }}
        onDeleteDraft={(deletedDraftId) => {
          if (deletedDraftId === draftId) {
            setDraftId(null);
            setIsDraft(false);
            // Limpar formulário
            if (areaType !== 'purchases') {
              setTitle('');
            }
            setObject('');
            setItemsDescription('');
            setDescription('');
            setPriority('p3');
            setUrgency('');
            setWorkflowId('');
            setGroupId('');
            setCustomFieldValues({});
            setCustomFieldSavedFiles({});
            setCustomFieldFiles({});
          }
        }}
      />
    )}
    </>
  );
};

const getStatusColor = (status: string) => {
  const colors: { [key: string]: string } = {
    novo: 'warning',
    aberto: 'warning',
    em_andamento: 'info',
    aguardando_informacao_usuario: 'secondary',
    aguardando_terceiros: 'secondary',
    aguardando_evidencia: 'secondary',
    aguardando_problema: 'secondary',
    aguardando_implantacao: 'secondary',
    aguardando_aprovacao: 'primary',
    aguardando_reenvio: 'danger',
    resolvido: 'success',
    cancelado: 'danger',
    fechado: 'dark',
  };
  return colors[status] || 'secondary';
};

const getStatusLabel = (status: string): string => {
  const labels: { [key: string]: string } = {
    novo: 'Aberto',
    aberto: 'Aberto',
    em_andamento: 'Em Andamento',
    aguardando_informacao_usuario: 'Aguardando Informação do Usuário',
    aguardando_terceiros: 'Aguardando Terceiros',
    aguardando_evidencia: 'Aguardando Evidência',
    aguardando_problema: 'Aguardando Problema',
    aguardando_implantacao: 'Aguardando Implantação',
    aguardando_aprovacao: 'Aguardando Aprovação',
    aguardando_reenvio: 'Aguardando Reenvio',
    resolvido: 'Resolvido',
    cancelado: 'Cancelado',
    fechado: 'Fechado',
  };
  return labels[status] || status;
};

const getPriorityColor = (priority: string | undefined) => {
  const colors: { [key: string]: string } = {
    p1: 'danger',
    p2: 'warning',
    p3: 'info',
    p4: 'success',
  };
  return colors[priority ?? ''] || 'secondary';
};

export default Dashboard;
